import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTaskById,
  uploadTaskRecords,
  updateTaskRecord,
  deleteTaskRecord,
  verifyEmail,
  extractWebsite,
  makeCall,
  getCallDetails,
  bulkOperations,
  sendTaskEmail,
  sendBulkEmails,
  getCampaigns,
  getUserCredits,
  scrapeLinkedInProfile,
  scrapeLinkedInCompany,
  uploadValidationRules,
  getValidationRules,
  validateAllRecords,
  validateSelectedRecords,
  getValidationStatus,
  cancelValidation,
  getValidationProgress,
  updateTask,
  getTaskRecordsForExport,
  getRetellAgents,
  getRetellPhoneNumbers
} from '../services/api';
import ValidationDetailModal from './ValidationDetailModal';
import EmailResponseModal from './EmailResponseModal';
import ValidationProgressPanel from './ValidationProgressPanel';

const formatRetellAgentLabel = (agent) => {
  const rawName = agent?.agent_name || agent?.agent_id || '';
  return rawName.replace(/varification/gi, 'verification');
};

// Sample validation rules for download (rule.txt)
const SAMPLE_VALIDATION_RULES = `Geographical locations: India
Head Quarter: Yes but only if the local address and contact number isn't available No tool-free numbers at all
Make sure if there are any fields that is missing such as address and contact number, revenue you need to do web search on the sources you know and fill those as-well
anything that you change, replace, or add needs have the Evidence_Sources and the Evidence_Sources link so that it can be revalidate at all times
Job Titles, founder, Manager
Departments, all
Company Size, 11-50
Industries: all`;

// Validation Progress Indicator Component
const ValidationProgressIndicator = ({ record, taskId }) => {
  const [progress, setProgress] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    if (record.validation_status === 'processing') {
      setIsPolling(true);
      const pollProgress = async () => {
        try {
          const response = await getValidationProgress(taskId, record.id);
          if (response.success && response.data) {
            setProgress(response.data);
          }
        } catch (error) {
          console.error('Error fetching validation progress:', error);
        }
      };

      // Poll immediately
      pollProgress();
      
      // AUTO-RELOAD DISABLED: Then poll every 2 seconds
      // const interval = setInterval(pollProgress, 2000);
      
      return () => {
        // clearInterval(interval);
        setIsPolling(false);
      };
    } else {
      setIsPolling(false);
      setProgress(null);
    }
  }, [record.validation_status, record.id, taskId]);

  // Get progress text
  const getProgressText = () => {
    if (!progress) return 'Processing...';
    
    if (progress.step) {
      return progress.step;
    }
    
    if (progress.stage_name) {
      return `Stage ${progress.current_stage}: ${progress.stage_name}`;
    }
    
    return 'Processing...';
  };

  const progressText = getProgressText();

  return (
    <span 
      className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-900/50 border border-yellow-500/50 group relative" 
      title={`Validation: ${progressText} - Click for details`}
    >
      <span className="text-yellow-400 text-[10px] animate-pulse">⏳</span>
      {/* Tooltip showing current step */}
      {progress && progress.step && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 border border-yellow-500/30">
          {progressText}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
            <div className="border-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </span>
  );
};

function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [operationLoading, setOperationLoading] = useState({});
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedRecordForEmail, setSelectedRecordForEmail] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showSubjectLineModal, setShowSubjectLineModal] = useState(false);
  const [customSubjectLine, setCustomSubjectLine] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('paste'); // 'paste' or 'file'
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'emailed', 'not-emailed', 'opened', 'responded', 'called', 'website-extracted'
  const [filterVerification, setFilterVerification] = useState('all'); // 'all', 'verified', 'rejected', 'pending'
  const [filterEmailStage, setFilterEmailStage] = useState('all'); // 'all', 'pass', 'reject', 'pending', 'not-sent'
  const [filterValidationStage, setFilterValidationStage] = useState('all'); // 'all', 'pass', 'reject', 'pending', 'not-validated'
  const [filterCallStage, setFilterCallStage] = useState('all'); // 'all', 'pass', 'reject', 'pending', 'not-called'
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at', 'name', 'email', 'company'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  
  // Calling Modal state
  const [showCallingModal, setShowCallingModal] = useState(false);
  const [callingProgress, setCallingProgress] = useState({ current: 0, total: 0, status: '', currentRecord: null });
  const [callingResults, setCallingResults] = useState([]);
  const [selectedCallRecord, setSelectedCallRecord] = useState(null); // For viewing call details
  const [callDetails, setCallDetails] = useState(null); // Call details (recording, transcription)
  const [loadingCallDetails, setLoadingCallDetails] = useState(false);
  const callRecordIdRef = useRef(null); // Track call record ID to prevent infinite loops
  const validationLastAlertRef = useRef(0); // For "every 10 records" alert during validation
  const [recordCallDetails, setRecordCallDetails] = useState({}); // Store call details for each record ID
  const [loadingRecordCallDetails, setLoadingRecordCallDetails] = useState({}); // Loading state per record
  const [expandedCallDetails, setExpandedCallDetails] = useState(new Set()); // Track which records have call details expanded
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50); // 50, 100, 200, 500
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [credits, setCredits] = useState(null); // calling credits
  const [validationCredits, setValidationCredits] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded rows
  const [websiteScrapingResults, setWebsiteScrapingResults] = useState({}); // Store website scraping results
  const [sidebarOpen, setSidebarOpen] = useState(false); // Sidebar visibility (closed by default)
  const [showCSVHeaders, setShowCSVHeaders] = useState(false); // CSV headers visibility
  
  // Top-level operations state
  const [showBulkOperationsPanel, setShowBulkOperationsPanel] = useState(false);
  const [bulkOperationType, setBulkOperationType] = useState(null); // 'email', 'validation', 'call', 'master'
  const [showEmailSettingsModal, setShowEmailSettingsModal] = useState(false);
  const [bulkEmailSubjectLine, setBulkEmailSubjectLine] = useState('');
  const [bulkEmailCampaignId, setBulkEmailCampaignId] = useState(null);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, status: '' });
  const [emailFilter, setEmailFilter] = useState('not-emailed'); // 'all', 'not-emailed', 'has-email'
  
  // Validation state
  const [validationRules, setValidationRules] = useState('');
  const [validationFileName, setValidationFileName] = useState('');
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationRunTotal, setValidationRunTotal] = useState(null); // selected/run total so progress shows "0/3" not "0/19"
  const [validationFilter, setValidationFilter] = useState('all'); // 'all', 'selected', 'verified', 'unverified'
  const [selectedValidationRecord, setSelectedValidationRecord] = useState(null); // For validation detail modal
  const [showValidationDetailModal, setShowValidationDetailModal] = useState(false);
  const [selectedEmailRecord, setSelectedEmailRecord] = useState(null); // For email response modal
  const [showEmailResponseModal, setShowEmailResponseModal] = useState(false);
  
  // Task edit state
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editTaskData, setEditTaskData] = useState({
    task_name: '',
    description: '',
    calling_by: '',
    agent_name: '',
    retell_from_number: '',
    script: '',
    caller_company: '',
    assets: ''
  });

  const [retellAgents, setRetellAgents] = useState([]);
  const [retellPhoneNumbers, setRetellPhoneNumbers] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [updatingTask, setUpdatingTask] = useState(false);
  
  // Master queue state
  const [masterQueueSteps, setMasterQueueSteps] = useState([
    { id: 'validation', name: 'Lead Validation', enabled: true, status: 'pending' },
    { id: 'email', name: 'Send Emails', enabled: true, status: 'pending' },
    { id: 'call', name: 'Voice Calls', enabled: false, status: 'pending' }
  ]);
  const [showMasterQueueModal, setShowMasterQueueModal] = useState(false);

  useEffect(() => {
    loadTask();
    loadCampaigns();
    loadCredits();
    loadValidationRules();
    loadRetellAgents();
    loadRetellPhoneNumbers();
    
    // Check if validation is in progress and restore progress state
    const checkAndRestoreValidationProgress = async () => {
      try {
        const statusResponse = await getValidationStatus(id);
        if (statusResponse.success && statusResponse.data) {
          const statusData = statusResponse.data;
          // In progress if any record is processing OR batch cursor still running (so refresh shows correct state)
          const processing = statusData.processing || 0;
          const cb = statusData.current_batch ?? 0;
          const tb = statusData.total_batches ?? 0;
          const hasProcessing = processing > 0 || (tb > 0 && cb > 0 && cb <= tb);

          if (hasProcessing) {
            setIsValidating(true);
            setIsBulkProcessing(true);

            const totalRun = statusData.total_run != null ? statusData.total_run : statusData.total;
            const total = totalRun != null ? totalRun : (statusData.total || 0);
            if (totalRun != null) setValidationRunTotal(totalRun);
            let completedRun = (statusData.qualified_run ?? statusData.qualified ?? 0) + (statusData.rejected_run ?? statusData.rejected ?? 0) + (statusData.error_run ?? statusData.error ?? 0);
            if (totalRun != null && statusData.qualified_run == null && statusData.rejected_run == null && statusData.error_run == null) {
              const taskCompleted = (statusData.qualified || 0) + (statusData.rejected || 0) + (statusData.error || 0);
              completedRun = Math.min(taskCompleted, totalRun);
            }
            const completed = totalRun != null ? completedRun : (statusData.qualified || 0) + (statusData.rejected || 0) + (statusData.error || 0);
            const processingCount = statusData.processing_run ?? statusData.processing ?? 0;
            const stepInfo = statusData.current_step ? ` • ${statusData.current_step}` : '';
            setBulkProgress({
              current: completed,
              total,
              status: `${processingCount} total record(s) in processing • ${completed} completed of ${total}${stepInfo}`
            });
          }
        }
      } catch (err) {
        console.error('Error checking validation progress:', err);
      }
    };
    
    checkAndRestoreValidationProgress();
    
    // NO AUTO-RELOAD - removed as per user request
  }, [id]); // Only depend on id, not isValidating

  const refreshRetellData = async () => {
    await Promise.all([loadRetellAgents(), loadRetellPhoneNumbers()]);
  };

  // Load validation rules for this task
  const loadValidationRules = async () => {
    try {
      const response = await getValidationRules(id);
      if (response.success && response.data) {
        setValidationRules(response.data.validation_rules || '');
        setValidationFileName(response.data.file_name || '');
      }
    } catch (err) {
      console.error('Error loading validation rules:', err);
    }
  };

  // Load validation status
  const loadValidationStatusData = async () => {
    try {
      const response = await getValidationStatus(id);
      if (response.success) {
        setValidationStatus(response.data);
      }
    } catch (err) {
      console.error('Error loading validation status:', err);
    }
  };

  // Start polling for validation progress (reusable function)
  const startValidationProgressPolling = () => {
    // AUTO-RELOAD DISABLED: Clear any existing polling interval
    // if (window.validationPollInterval) {
    //   clearInterval(window.validationPollInterval);
    // }
    
    // AUTO-RELOAD DISABLED: Polling interval commented out
    // const pollInterval = setInterval(async () => {
    //   try {
    //     const statusResponse = await getValidationStatus(id);
    //     if (statusResponse.success) {
    //       setValidationStatus(statusResponse.data);

    //       const batchInfo = statusResponse.data;
    //       const processed = (batchInfo.qualified || 0) + (batchInfo.rejected || 0) + (batchInfo.error || 0);
    //       const total = batchInfo.total || 0;

    //       // Update bulk progress with batch info
    //       if (batchInfo.total_batches > 0 && batchInfo.current_batch) {
    //         const batchStatus = `Batch ${batchInfo.current_batch}/${batchInfo.total_batches}`;
    //         const stepInfo = batchInfo.current_step ? ` - ${batchInfo.current_step}` : '';
    //         setBulkProgress({
    //           current: processed,
    //           total: total,
    //           status: `Validating... ${batchStatus}${stepInfo}`
    //         });
    //       } else {
    //         // Get progress details for processing records
    //         if (batchInfo.processing_progress && batchInfo.processing_progress.length > 0) {
    //           const progress = batchInfo.processing_progress[0];
    //           if (progress) {
    //             const stepName = progress.step || progress.stage_name || 'Processing...';
    //             const stageInfo = progress.current_stage
    //               ? `Stage ${progress.current_stage}: ${stepName}`
    //               : stepName;
    //             setBulkProgress({
    //               current: batchInfo.processing || processed,
    //               total: total,
    //               status: `Validating... ${stageInfo}`
    //             });
    //           }
    //         } else if (batchInfo.processing > 0) {
    //           setBulkProgress({
    //             current: batchInfo.processing,
    //             total: total,
    //             status: batchInfo.current_step || 'Validating... Processing records'
    //           });
    //         }
    //       }

    //       // Check if validation is complete
    //       const isComplete = (processed >= total && batchInfo.processing === 0) || 
    //                         (batchInfo.current_batch && batchInfo.total_batches && batchInfo.current_batch >= batchInfo.total_batches && batchInfo.processing === 0);
          
    //       if (isComplete) {
    //         clearInterval(pollInterval);
    //         window.validationPollInterval = null;
    //         setIsValidating(false);
    //         setIsBulkProcessing(false);
    //         loadTask(); // Load updated records once
    //         if (validationFilter === 'selected') {
    //           setSelectedRecords([]);
    //         }
    //         validationLastAlertRef.current = processed;
    //         alert(`✓ Validation completed! ${batchInfo.qualified || 0} qualified, ${batchInfo.rejected || 0} rejected`);
    //       }
    //     }
    //   } catch (err) {
    //     console.error('Error polling validation progress:', err);
    //   }
    // }, 3000); // Poll every 3 seconds
    
    // AUTO-RELOAD DISABLED: Store interval reference globally so it can be cleared
    // window.validationPollInterval = pollInterval;
    
    // AUTO-RELOAD DISABLED: Stop polling after 10 minutes as safety
    // setTimeout(() => {
    //   if (window.validationPollInterval) {
    //     clearInterval(window.validationPollInterval);
    //     window.validationPollInterval = null;
    //   }
    //   setIsValidating(false);
    //   setIsBulkProcessing(false);
    // }, 600000);
  };

  // Stop validation and allow starting again (calls server to clear state)
  const handleRestartValidation = async () => {
    if (window.validationPollInterval) {
      clearInterval(window.validationPollInterval);
      window.validationPollInterval = null;
    }
    try {
      await cancelValidation(id);
    } catch (err) {
      console.error('Error cancelling validation:', err);
    }
    setValidationRunTotal(null);
    setIsValidating(false);
    setIsBulkProcessing(false);
    setShowValidationModal(true);
  };

  const loadCredits = async () => {
    try {
      const response = await getUserCredits();
      if (response.success) {
        setCredits(response.callingCredit ?? response.credits ?? 0);
        setValidationCredits(response.validationCredit ?? response.credits ?? 0);
        setIsAdmin(response.role === 'admin');
      }
    } catch (err) {
      console.error('Error loading credits:', err);
    }
  };

  const loadRetellAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await getRetellAgents();
      if (response.success && Array.isArray(response.agents)) {
        const sortedAgents = [...response.agents].sort(
          (a, b) => Number(b.last_modification_timestamp || 0) - Number(a.last_modification_timestamp || 0)
        );

        const uniqueAgentsMap = new Map();
        sortedAgents.forEach((agent) => {
          const uniqueId = agent.agent_id || '';
          if (uniqueId && !uniqueAgentsMap.has(uniqueId)) {
            uniqueAgentsMap.set(uniqueId, agent);
          }
        });

        setRetellAgents(Array.from(uniqueAgentsMap.values()));
      }
    } catch (e) {
      console.error('Could not load Retell agents:', e);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadRetellPhoneNumbers = async () => {
    setLoadingPhoneNumbers(true);
    try {
      const response = await getRetellPhoneNumbers();
      if (response.success && Array.isArray(response.phoneNumbers)) {
        setRetellPhoneNumbers(response.phoneNumbers);
      }
    } catch (e) {
      console.error('Could not load Retell phone numbers:', e);
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const response = await getCampaigns();
      if (response.success) {
        setCampaigns(response.campaigns || []);
      }
    } catch (err) {
      console.error('Error loading campaigns:', err);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // ============ TOP-LEVEL BULK OPERATIONS ============

  // Handle validation rules file upload
  const handleValidationFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.txt')) {
      setError('Please upload a .txt file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      setValidationRules(content);
      setValidationFileName(file.name);
      
      // Save to database
      try {
        await uploadValidationRules(id, content, file.name);
        setError(null);
        alert('✓ Validation rules uploaded successfully!');
      } catch (err) {
        setError('Failed to save validation rules: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  // Download sample rule.txt file
  const handleDownloadSampleRules = () => {
    const blob = new Blob([SAMPLE_VALIDATION_RULES], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-rule.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Get validatable records count based on filter
  const getValidatableRecordsCount = () => {
    if (!task?.records) return 0;
    
    switch (validationFilter) {
      case 'selected':
        // Only count if records are actually selected
        return selectedRecords.length;
      case 'verified':
        return task.records.filter(r => r.manual_verification_status === 'verified').length;
      case 'unverified':
        return task.records.filter(r => r.manual_verification_status !== 'verified' && r.manual_verification_status !== 'rejected').length;
      case 'all':
      default:
        return task.records.length;
    }
  };

  // Start bulk validation - validates records based on filter
  const handleStartValidation = async () => {
    // CRITICAL: Validate selection before proceeding
    if (validationFilter === 'selected') {
      if (!selectedRecords || selectedRecords.length === 0) {
        setError('Please select at least one record from the table before validating');
        return;
      }
    }
    
    const recordCount = getValidatableRecordsCount();
    
    if (recordCount === 0) {
      setError('No records to validate');
      return;
    }

    // Check validation credits: need at least 1 batch worth to start (credits deducted per batch as processing)
    const batchSize = 10; // Matches server VALIDATION_BATCH_SIZE default
    const creditsNeededToStart = Math.min(batchSize, recordCount);
    if (validationCredits !== null && validationCredits < creditsNeededToStart) {
      setError(`Insufficient validation credits. You have ${validationCredits} but need at least ${creditsNeededToStart} to start the first batch (credits are deducted per batch as validation runs)`);
      return;
    }
    
    setIsValidating(true);
    setIsBulkProcessing(true);
    setValidationRunTotal(recordCount);
    setBulkProgress({ 
      current: 0, 
      total: recordCount, 
      status: `Starting validation for ${recordCount} record(s)...` 
    });
    
    // Close modal
    setShowValidationModal(false);
    
    try {
      // CRITICAL: Properly determine which records to validate based on filter
      let recordIdsToValidate = null;
      
      if (validationFilter === 'selected') {
        // CRITICAL: Use selected records - ensure they are actual IDs and remove duplicates
        recordIdsToValidate = Array.isArray(selectedRecords) && selectedRecords.length > 0 
          ? [...new Set(selectedRecords.filter(id => id != null && !isNaN(parseInt(id))).map(id => parseInt(id)))]
          : [];
        
        if (recordIdsToValidate.length === 0) {
          setError('No valid records selected. Please select records from the table.');
          setValidationRunTotal(null);
          setIsValidating(false);
          setIsBulkProcessing(false);
          return;
        }
        
        console.log(`🎯 Validating ${recordIdsToValidate.length} SELECTED records (IDs: ${recordIdsToValidate.join(', ')})`);
      } else if (validationFilter === 'all') {
        // For 'all', send empty array with filter='all' to tell backend to get all records
        recordIdsToValidate = [];
        console.log(`🎯 Validating ALL records (${recordCount} total)`);
      } else if (validationFilter === 'verified') {
        // For 'verified', send empty array with filter='verified'
        recordIdsToValidate = [];
        console.log(`🎯 Validating VERIFIED records (${recordCount} total)`);
      } else if (validationFilter === 'unverified') {
        // For 'unverified', send empty array with filter='unverified'
        recordIdsToValidate = [];
        console.log(`🎯 Validating UNVERIFIED records (${recordCount} total)`);
      }
      
      const response = await validateSelectedRecords(id, recordIdsToValidate, validationFilter);
        
      if (response.success) {
        loadCredits();
        setBulkProgress({
          current: 0,
          total: recordCount,
          status: `${recordCount} total record(s) in processing • 0 completed of ${recordCount}`
        });
        validationLastAlertRef.current = 0;

        console.log(`✅ Validation started:`, {
          filter: validationFilter,
          recordIdsCount: recordIdsToValidate?.length || 0,
          recordIds: recordIdsToValidate,
          expectedCount: recordCount
        });
        // No auto-refresh: user refreshes the page when they want to see updated counts
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setError(msg || 'Failed to start validation');
      setValidationRunTotal(null);
      setIsValidating(false);
      setIsBulkProcessing(false);
      if (err.response?.status === 402) {
        loadCredits();
      }
      if (err.response?.status === 409) {
        setShowValidationModal(true);
      }
    }
  };

  // Handle bulk email with custom subject line
  const handleBulkEmailStart = () => {
    if (campaigns.length === 0) {
      setError('No campaigns available. Please create a campaign first.');
      return;
    }
    setShowEmailSettingsModal(true);
  };

  const handleBulkEmailSend = async () => {
    if (!bulkEmailCampaignId) {
      setError('Please select a campaign');
      return;
    }
    
    setIsBulkProcessing(true);
    setShowEmailSettingsModal(false);
    
    // Calculate records based on filter
    const recordsToEmail = getEmailableRecordsCount();
    setBulkProgress({ current: 0, total: recordsToEmail, status: 'Sending emails...' });
    
    try {
      // If filter is "selected", pass the selected record IDs
      const recordIdsToSend = emailFilter === 'selected' ? selectedRecords : null;
      
      const response = await sendBulkEmails(
        id, 
        bulkEmailCampaignId, 
        bulkEmailSubjectLine || null,
        recordIdsToSend, // pass selected record_ids if "selected" filter
        emailFilter // use the selected filter
      );
      
      if (response.success) {
        setBulkProgress({ ...bulkProgress, status: 'Bulk email started in background. Refreshing...' });
        // Refresh after a delay
        setTimeout(() => {
          loadTask();
          setIsBulkProcessing(false);
          setSelectedRecords([]); // Clear selection after sending
          alert(`✓ Bulk email sending started! ${response.data?.total_records || 0} records queued.`);
        }, 3000);
      }
    } catch (err) {
      setError('Failed to start bulk email: ' + err.message);
      setIsBulkProcessing(false);
    }
  };

  // Get count of records that will receive emails based on current filter
  const getEmailableRecordsCount = () => {
    if (!task?.records) return 0;
    switch (emailFilter) {
      case 'selected':
        // Only count selected records that have an email
        return task.records.filter(r => selectedRecords.includes(r.id) && (r.email || r.business_email)).length;
      case 'all':
        return task.records.filter(r => r.email || r.business_email).length;
      case 'has-email':
        return task.records.filter(r => r.email || r.business_email).length;
      case 'qualified':
        return task.records.filter(r => (r.email || r.business_email) && r.validation_status === 'qualified').length;
      case 'not-emailed':
      default:
        return task.records.filter(r => (r.email || r.business_email) && !r.emailed).length;
    }
  };

  // Handle Master Queue (sequential operations)
  const handleMasterQueueStart = async () => {
    setShowMasterQueueModal(false);
    setIsBulkProcessing(true);
    
    const enabledSteps = masterQueueSteps.filter(s => s.enabled);
    
    for (let i = 0; i < enabledSteps.length; i++) {
      const step = enabledSteps[i];
      setBulkProgress({ 
        current: i + 1, 
        total: enabledSteps.length, 
        status: `Running: ${step.name}...` 
      });
      
      // Update step status
      setMasterQueueSteps(prev => prev.map(s => 
        s.id === step.id ? { ...s, status: 'processing' } : s
      ));
      
      try {
        if (step.id === 'validation') {
          await validateAllRecords(id);
          // Wait for validation to complete (simplified - in production use proper polling)
          await new Promise(resolve => setTimeout(resolve, 10000));
        } else if (step.id === 'email') {
          if (bulkEmailCampaignId) {
            await sendBulkEmails(id, bulkEmailCampaignId, bulkEmailSubjectLine, null, 'qualified');
          }
        } else if (step.id === 'call') {
          // Voice calls - use bulk operation with correct parameters
          const recordIds = selectedRecords.length > 0 ? selectedRecords : task.records.filter(r => r.phone_number && !r.called).map(r => r.id);
          if (recordIds.length > 0) {
            await bulkOperations(id, recordIds, 'call');
          }
        }
        
        setMasterQueueSteps(prev => prev.map(s => 
          s.id === step.id ? { ...s, status: 'completed' } : s
        ));
      } catch (err) {
        setMasterQueueSteps(prev => prev.map(s => 
          s.id === step.id ? { ...s, status: 'failed' } : s
        ));
        console.error(`Error in ${step.name}:`, err);
      }
    }
    
    setIsBulkProcessing(false);
    loadTask();
    alert('✓ Master queue completed!');
  };

  // Insert dynamic variable into subject line
  const insertVariable = (variable) => {
    setBulkEmailSubjectLine(prev => prev + variable);
  };

  // ============ END BULK OPERATIONS ============

  const handleEmailClick = (record) => {
    if (!record.email) {
      setError('No email address found for this record');
      return;
    }
    setSelectedRecordForEmail(record);
    setShowCampaignModal(true);
  };

  const handleCampaignSelect = (campaign) => {
    setSelectedCampaignId(campaign.id);
    setSelectedCampaign(campaign);
    setCustomSubjectLine(campaign.subject_line || '');
    setShowCampaignModal(false);
    setShowSubjectLineModal(true);
  };

  const handleSendEmail = async (campaignId = null, subjectLine = null) => {
    if (!selectedRecordForEmail) return;

    const finalCampaignId = campaignId || selectedCampaignId;
    if (!finalCampaignId) {
      setError('Please select a campaign');
      return;
    }

    // Confirm the email will be sent to the record's email
    if (!selectedRecordForEmail.email) {
      setError('No email address found in this record');
      setShowCampaignModal(false);
      setShowSubjectLineModal(false);
      setSelectedRecordForEmail(null);
      return;
    }

    setShowSubjectLineModal(false);
    setOperationLoading({ ...operationLoading, [selectedRecordForEmail.id]: 'send-email' });
    setError(null);

    try {
      // Send email using the record's email automatically (no need to select)
      const response = await sendTaskEmail(id, selectedRecordForEmail.id, finalCampaignId, null, null, subjectLine || customSubjectLine);
      if (response.success) {
        loadTask();
        // Show success message
        setTimeout(() => {
          alert(`✓ Email sent successfully to ${selectedRecordForEmail.email} using campaign "${response.data?.campaign_name || 'selected campaign'}"`);
        }, 100);
      } else {
        setError(response.message || 'Failed to send email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setError(err.response?.data?.message || 'Failed to send email');
    } finally {
      setOperationLoading({ ...operationLoading, [selectedRecordForEmail.id]: null });
      setSelectedRecordForEmail(null);
      setSelectedCampaignId(null);
      setSelectedCampaign(null);
      setCustomSubjectLine('');
    }
  };

  const loadTask = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 Loading task:', id);
      const response = await getTaskById(id);
      console.log('📥 Task loaded:', response);
      if (response.success) {
        console.log('✅ Task loaded successfully:', {
          id: response.data.id,
          task_name: response.data.task_name,
          calling_by: response.data.calling_by,
          caller_company: response.data.caller_company,
          assets: response.data.assets
        });
        setTask(response.data);
        
        // Populate edit form data
        setEditTaskData({
          task_name: response.data.task_name || '',
          description: response.data.description || '',
          calling_by: response.data.calling_by || '',
          agent_name: response.data.agent_name || '',
          retell_from_number: response.data.retell_from_number || '',
          script: response.data.script || '',
          caller_company: response.data.caller_company || 'The Tech Marketer',
          assets: response.data.assets || 'Signal-First Demand Generation content from Market Wavegen',
          purpose_of_call: response.data.purpose_of_call || 'Discovery Call'
        });
      }
    } catch (err) {
      console.error('❌ Error loading task:', err);
      setError(err.response?.data?.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    console.log('🚀 handleUpdateTask called (TaskDetail)');
    console.log('📋 Current form values:', editTaskData);
    
    if (!editTaskData.task_name.trim()) {
      setError('Task name is required');
      return;
    }

    setUpdatingTask(true);
    setError(null);
    try {
      // Prepare update data - send ALL fields with actual values
      const finalPurpose = editTaskData.purpose_of_call === 'Custom' ? editTaskData.custom_purpose : editTaskData.purpose_of_call;
      const updateData = {
        task_name: editTaskData.task_name.trim(),
        description: editTaskData.description ? editTaskData.description.trim() : null,
        calling_by: editTaskData.calling_by ? editTaskData.calling_by.trim() : '',
        agent_name: editTaskData.agent_name ? editTaskData.agent_name.trim() : '',
        retell_from_number: editTaskData.retell_from_number || null,
        script: editTaskData.script ? editTaskData.script.trim() : null,
        caller_company: editTaskData.caller_company ? editTaskData.caller_company.trim() : '',
        assets: editTaskData.assets ? editTaskData.assets.trim() : '',
        purpose_of_call: finalPurpose || 'Discovery Call'
      };
      
      console.log('📤 Sending update request:', {
        taskId: id,
        updateData: updateData,
        originalTask: task
      });
      
      const response = await updateTask(id, updateData);
      
      console.log('📥 Update response received:', response);
      
      if (response.success) {
        console.log('✅ Update successful, closing modal and reloading task');
        setShowEditTaskModal(false);
        await loadTask(); // Reload task to get updated data
        console.log('✅ Task reloaded');
      } else {
        console.error('❌ Update failed:', response.message);
        setError(response.message || 'Failed to update task');
      }
    } catch (err) {
      console.error('❌ Error updating task:', {
        error: err,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.message || err.message || 'Failed to update task');
    } finally {
      setUpdatingTask(false);
      console.log('🏁 Update process finished');
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    // Parse headers - handle quoted values and commas
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]).map(h => 
      h.toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/^["']|["']$/g, '')
    );

    const records = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue; // Skip empty lines
      
      const values = parseCSVLine(lines[i]);
      const record = {};
      
      headers.forEach((header, index) => {
        let value = (values[index] || '').trim().replace(/^["']|["']$/g, '');
        
        // Map various header formats to our field names
        const fieldMap = {
          // Name fields
          'first_name': 'first_name',
          'firstname': 'first_name',
          'first': 'first_name',
          'last_name': 'last_name',
          'lastname': 'last_name',
          'last': 'last_name',
          'full_name': 'full_name',
          'fullname': 'full_name',
          'name': 'full_name',
          
          // Email fields
          'business_email': 'business_email',
          'businessemail': 'business_email',
          'email': 'email',
          
          // Domain
          'domain': 'domain',
          
          // Job Title
          'job_title': 'job_title',
          'jobtitle': 'job_title',
          'job': 'job_title',
          'title': 'job_title',
          
          // Company fields
          'company_name': 'company_name',
          'companyname': 'company_name',
          'company': 'company_name',
          
          // Industry
          'industry_type': 'industry_type',
          'industrytype': 'industry_type',
          'industry': 'industry_type',
          
          // Revenue
          'revenue_size': 'revenue_size',
          'revenuesize': 'revenue_size',
          'revenue': 'revenue_size',
          
          // Employee Size
          'employee_size': 'employee_size',
          'employeesize': 'employee_size',
          'employees': 'employee_size',
          'employee_count': 'employee_size',
          
          // Phone
          'phone_number': 'phone_number',
          'phonenumber': 'phone_number',
          'phone': 'phone_number',
          
          // Address fields
          'address_street': 'address_street',
          'addressstreet': 'address_street',
          'street': 'address_street',
          'address': 'address_street',
          'city': 'city',
          'zip_code': 'zip_code',
          'zipcode': 'zip_code',
          'zip': 'zip_code',
          'postal_code': 'zip_code',
          'state': 'state',
          'country': 'country',
          
          // LinkedIn URLs
          'linkedin_url_for_contact': 'linkedin_url_contact',
          'linkedinurlforcontact': 'linkedin_url_contact',
          'linkedin_url_contact': 'linkedin_url_contact',
          'linkedin_contact': 'linkedin_url_contact',
          'linkedin_url_for_company': 'linkedin_url_company',
          'linkedinurlforcompany': 'linkedin_url_company',
          'linkedin_url_company': 'linkedin_url_company',
          'linkedin_company': 'linkedin_url_company',
          'linkedin_url': 'linkedin_url_contact', // Default to contact if just linkedin_url
          'linkedinurl': 'linkedin_url_contact',
          'linkedin': 'linkedin_url_contact',
          'linked_in_url': 'linkedin_url_contact',
          
          // Company website
          'company_website': 'company_website',
          'companywebsite': 'company_website',
          'website': 'company_website',
          'company_url': 'company_website'
        };
        
        const mappedField = fieldMap[header] || header;
        // Set value, converting empty strings to null for consistency
        record[mappedField] = (value && value.trim()) || null;
        
        // Also set linkedin_url as fallback if linkedin_url_contact is set
        if (mappedField === 'linkedin_url_contact' && record[mappedField]) {
          record.linkedin_url = record[mappedField];
        }
      });
      
      // Debug log first record to verify parsing
      if (records.length === 0) {
        console.log('CSV Parsing Debug - Headers:', headers);
        console.log('CSV Parsing Debug - First Record:', JSON.stringify(record, null, 2));
      }
      
      records.push(record);
    }

    return records;
  };

  const downloadSampleCSV = () => {
    // CSV field names matching the expected format
    const headers = [
      'First Name',
      'Last Name',
      'Business Email',
      'domain',
      'Job Title',
      'Company Name',
      'Industry Type',
      'Revenue Size',
      'Employee Size',
      'Phone Number',
      'Address Street',
      'City',
      'State',
      'Zip Code',
      'Country',
      'Linkedin URL For Contact',
      'Linkedin URL For Company'
    ];
    
    // Sample data row
    const sampleData = [
      'John',
      'Doe',
      'john.doe@example.com',
      'example.com',
      'Software Engineer',
      'Acme Inc',
      'Technology',
      '10M-50M',
      '100-500',
      '+1234567890',
      '123 Main Street',
      'San Francisco',
      'CA',
      '94105',
      'United States',
      'https://linkedin.com/in/johndoe',
      'https://linkedin.com/company/acme'
    ];
    
    // Create CSV content (use commas, wrap in quotes if needed)
    const csvContent = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `task_${id}_sample.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const records = parseCSV(csvText);
        setUploadData(JSON.stringify(records, null, 2));
        setError(null);
      } catch (err) {
        setError(`Error parsing CSV: ${err.message}`);
        setSelectedFile(null);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setSelectedFile(null);
    };
    
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (uploadMethod === 'file' && !selectedFile && !uploadData.trim()) {
      setError('Please select a CSV file or paste data');
      return;
    }

    if (uploadMethod === 'paste' && !uploadData.trim()) {
      setError('Please paste or upload data');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      let records = [];
      
      if (uploadMethod === 'file' && selectedFile) {
        // File was already parsed in handleFileUpload
        try {
          records = JSON.parse(uploadData);
        } catch {
          setError('Error parsing uploaded file data');
          setUploading(false);
          return;
        }
      } else {
        // Try to parse as JSON first
        try {
          const parsed = JSON.parse(uploadData);
          records = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          // If not JSON, try CSV parsing
          records = parseCSV(uploadData);
        }
      }

      if (records.length === 0) {
        setError('No records found in the data');
        setUploading(false);
        return;
      }

      const response = await uploadTaskRecords(id, records);
      if (response.success) {
        // Check if there were any errors
        if (response.data && response.data.errors && response.data.errors.length > 0) {
          const errorMessages = response.data.errors.map(e => `Record ${e.index + 1}: ${e.error}`).join('\n');
          setError(`Some records failed to upload:\n${errorMessages}\n\n${response.data.inserted_count} record(s) were uploaded successfully.`);
          // Still reload to show successfully uploaded records
          loadTask();
        } else {
        setShowUploadModal(false);
        setUploadData('');
        setSelectedFile(null);
        setUploadMethod('paste');
        loadTask();
        }
      } else {
        setError(response.message || 'Failed to upload records');
      }
    } catch (err) {
      console.error('Error uploading:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload records');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      const response = await deleteTaskRecord(id, recordId);
      if (response.success) {
        loadTask();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete record');
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record.id);
    setEditFormData({
      first_name: record.first_name || '',
      last_name: record.last_name || '',
      full_name: record.full_name || '',
      email: record.email || '',
      business_email: record.business_email || '',
      domain: record.domain || '',
      job_title: record.job_title || '',
      company_name: record.company_name || '',
      industry_type: record.industry_type || '',
      revenue_size: record.revenue_size || '',
      employee_size: record.employee_size || '',
      phone_number: record.phone_number || '',
      address_street: record.address_street || '',
      city: record.city || '',
      state: record.state || '',
      zip_code: record.zip_code || '',
      country: record.country || '',
      linkedin_url: record.linkedin_url || record.linkedin_url_contact || '',
      linkedin_url_contact: record.linkedin_url_contact || '',
      linkedin_url_company: record.linkedin_url_company || '',
      company_website: record.company_website || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    const linkedinUrl = editFormData.linkedin_url_contact || editFormData.linkedin_url || '';
    if (!linkedinUrl || linkedinUrl.trim() === '') {
      setError('LinkedIn URL For Contact is required');
      return;
    }

    setError(null);
    try {
      const response = await updateTaskRecord(id, editingRecord, editFormData);
      if (response.success) {
        setEditingRecord(null);
        setEditFormData({});
        setShowEditModal(false);
        setError(null);
        loadTask();
      } else {
        setError(response.message || 'Failed to update record');
      }
    } catch (err) {
      console.error('Error updating record:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update record';
      setError(errorMessage);
    }
  };

  const handleCancelEdit = () => {
    setEditingRecord(null);
    setEditFormData({});
    setShowEditModal(false);
    setError(null);
  };

  const toggleRowExpansion = (recordId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId);
    } else {
      newExpanded.add(recordId);
      
      // If record has a call (called flag, call_log_id, or recording_url), fetch call details
      const record = task?.records?.find(r => r.id === recordId);
      if (record && (record.called || record.call_log_id || record.recording_url) && !recordCallDetails[recordId] && !loadingRecordCallDetails[recordId]) {
        fetchRecordCallDetails(recordId);
      }
    }
    setExpandedRows(newExpanded);
  };

  // Fetch call details for a specific record
  const fetchRecordCallDetails = async (recordId) => {
    if (loadingRecordCallDetails[recordId]) return; // Already loading
    
    setLoadingRecordCallDetails(prev => ({ ...prev, [recordId]: true }));
    try {
      const response = await getCallDetails(id, recordId);
      if (response.success) {
        setRecordCallDetails(prev => ({ ...prev, [recordId]: response.data }));
      }
    } catch (err) {
      console.error(`Error loading call details for record ${recordId}:`, err);
    } finally {
      setLoadingRecordCallDetails(prev => ({ ...prev, [recordId]: false }));
    }
  };

  const handleOperation = async (recordId, operation) => {
    setOperationLoading({ ...operationLoading, [recordId]: operation });
    try {
      let response;
      if (operation === 'extract-website') {
        response = await extractWebsite(id, recordId);
        // Store website scraping results
        if (response.success && response.data) {
          setWebsiteScrapingResults(prev => ({
            ...prev,
            [recordId]: response.data
          }));
        }
      } else if (operation === 'call') {
        // Use record data automatically - no need for manual input
        response = await makeCall(id, recordId);
      } else if (operation === 'scrape-linkedin-profile') {
        response = await scrapeLinkedInProfile(id, recordId);
      } else if (operation === 'scrape-linkedin-company') {
        response = await scrapeLinkedInCompany(id, recordId);
      }

      if (response && response.success) {
        loadTask();
        // Update calling credits if returned (for call operations)
        if (response.creditsRemaining !== null && response.creditsRemaining !== undefined && operation === 'call') {
          setCredits(response.creditsRemaining);
        }
        // Reload credits to ensure accuracy
        loadCredits();
      } else {
        setError(response.message || `Failed to ${operation}`);
        // Reload credits in case of error
        loadCredits();
      }
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${operation}`);
    } finally {
      setOperationLoading({ ...operationLoading, [recordId]: null });
    }
  };

  const handleBulkOperation = async (operation, campaignId = null) => {
    if (selectedRecords.length === 0) {
      setError('Please select at least one record');
      return;
    }

    console.log('🔄 Bulk operation requested:', { operation, campaignId, selectedCount: selectedRecords.length });

    // For email operation, show campaign selection modal
    if (operation === 'send-email') {
      if (!campaignId) {
        setShowBulkEmailModal(true);
        return;
      }
    } else {
      // Special confirmation for call operations
      if (operation === 'call') {
        const confirmMessage = `Make calls to ${selectedRecords.length} selected record(s)?\n\n` +
          `⚠️ Important:\n` +
          `• Calls will be processed sequentially (one at a time)\n` +
          `• Each call may take 30+ seconds to complete\n` +
          `• Total time: approximately ${Math.ceil(selectedRecords.length * 0.5)} minutes\n` +
          `• You need ${selectedRecords.length} call credit(s) (you have ${credits !== null ? credits : '?'})\n\n` +
          `Continue?`;
        if (!window.confirm(confirmMessage)) {
          return;
        }
      } else {
        if (!window.confirm(`Perform ${operation} on ${selectedRecords.length} selected records?`)) {
          return;
        }
      }
    }

    setOperationLoading({ ...operationLoading, bulk: operation });
    setError(null); // Clear previous errors
    
    try {
      console.log('📤 Sending bulk operation request:', { 
        taskId: id, 
        operation, 
        recordIds: selectedRecords, 
        campaignId 
      });
      
      const response = await bulkOperations(id, selectedRecords, operation, campaignId);
      
      console.log('📥 Bulk operation response:', response);
      
      if (response.success) {
        setSelectedRecords([]);
        setShowBulkEmailModal(false);
        loadTask();
        // Update calling credits if returned (for call operations)
        if (response.data.creditsRemaining !== null && response.data.creditsRemaining !== undefined && operation === 'call') {
          setCredits(response.data.creditsRemaining);
        }
        // Reload credits to ensure accuracy
        loadCredits();
        
        // Show detailed results for call operations
        if (operation === 'call') {
          const message = `Call operation completed:\n` +
            `✅ ${response.data.success_count} call(s) dispatched successfully\n` +
            `❌ ${response.data.failed_count} call(s) failed\n\n` +
            `Note: Calls are processed sequentially (one at a time).\n` +
            `Each call may take 30+ seconds to complete before the next call starts.`;
          alert(message);
          
          if (response.data.errors && response.data.errors.length > 0) {
            console.log('Call errors:', response.data.errors);
          }
        } else {
          alert(`Operation completed: ${response.data.success_count} succeeded, ${response.data.failed_count} failed`);
        }
      } else {
        setError(response.message || 'Operation failed');
        // Reload credits in case of error
        loadCredits();
      }
    } catch (err) {
      console.error('❌ Bulk operation error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to perform bulk operation';
      setError(errorMessage);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: errorMessage
      });
    } finally {
      setOperationLoading({ ...operationLoading, bulk: null });
    }
  };
  
  const handleBulkEmailSendOld = async (campaignId) => {
    console.log('📧 Bulk email send requested:', { campaignId, selectedRecords: selectedRecords.length });
    if (!campaignId) {
      setError('Please select a campaign');
      return;
    }
    await handleBulkOperation('send-email', campaignId);
  };

  // Handle sequential calling - one call at a time with 3-second delay between calls (RetellAI)
  const handleStartCalling = async () => {
    // CRITICAL: Only use selected records if any are selected
    let recordsToCall = [];
    
    if (selectedRecords.length > 0) {
      // Only process selected records
      recordsToCall = task.records.filter(r => 
        selectedRecords.includes(r.id) && 
        r.phone_number && 
        r.phone_number.trim() !== ''
      );
      
      if (recordsToCall.length === 0) {
        setError('Selected records do not have valid phone numbers');
        return;
      }
      
      console.log(`📞 Processing ${recordsToCall.length} SELECTED records for calling`);
    } else {
      // Fallback: use records with phone numbers that haven't been called
      recordsToCall = task.records.filter(r => r.phone_number && !r.called && r.phone_number.trim() !== '');
      
      if (recordsToCall.length === 0) {
        setError('No records with phone numbers available to call. Please select records from the table.');
        return;
      }
      
      console.log(`📞 Processing ${recordsToCall.length} records with phone numbers (not called yet)`);
    }
    
    // Check calling credits
    if (credits !== null && credits < recordsToCall.length) {
      setError(`Insufficient call credits. You have ${credits} but need ${recordsToCall.length}`);
      return;
    }

    // Confirm
    const confirmMessage = `Make calls to ${recordsToCall.length} record(s) via RetellAI?\n\n` +
      `⚠️ Important:\n` +
      `• Calls will be processed sequentially (one at a time)\n` +
      `• 3-second delay between each call initiation\n` +
      `• Each call takes 30-60 seconds to complete\n` +
      `• This will use ${recordsToCall.length} credit(s)\n\n` +
      `Continue?`;
    
    if (!window.confirm(confirmMessage)) return;

    setShowCallingModal(true);
    setIsBulkProcessing(true);
    setCallingResults([]);
    setCallingProgress({ 
      current: 0, 
      total: recordsToCall.length, 
      status: `Starting sequential calls (${recordsToCall.length} total)...`, 
      currentRecord: null 
    });

    const results = [];
    
    // Process calls sequentially with 3-second delay between each call initiation
    // This matches the pattern from /calls route (dispatchBulkCalls) - RetellAI recommended
    for (let i = 0; i < recordsToCall.length; i++) {
      const record = recordsToCall[i];
      const recordName = `${record.first_name || ''} ${record.last_name || ''}`.trim() || record.full_name || 'Unknown';
      
      // Update progress
      setCallingProgress({
        current: i + 1,
        total: recordsToCall.length,
        status: `Calling ${recordName} (${i + 1}/${recordsToCall.length})...`,
        currentRecord: record
      });

      try {
        // Make the call
        const response = await makeCall(id, record.id, {
          name: recordName,
          company: record.company_name,
          phone: record.phone_number
        });
        
        results.push({
          recordId: record.id,
          name: recordName,
          phone: record.phone_number,
          success: response.success,
          callId: response.data?.call_id,
          message: response.message || 'Call dispatched',
          response: response.data
        });
        
        setCallingResults([...results]);
        
        // CRITICAL: Wait 3 seconds before initiating next call (RetellAI recommended)
        if (i < recordsToCall.length - 1) {
          setCallingProgress(prev => ({ 
            ...prev, 
            status: `Waiting 3 seconds before next call... (${i + 1}/${recordsToCall.length} completed)` 
          }));
          await new Promise(resolve => setTimeout(resolve, 3000)); // 3 second delay between calls
        }
        
      } catch (err) {
        console.error(`❌ Call failed for record ${record.id}:`, err);
        
        results.push({
          recordId: record.id,
          name: recordName,
          phone: record.phone_number,
          success: false,
          message: err.response?.data?.message || err.message || 'Call failed',
          error: err.message
        });
        
        setCallingResults([...results]);
        
        // Even on error, wait 3 seconds before next call
        if (i < recordsToCall.length - 1) {
          setCallingProgress(prev => ({ 
            ...prev, 
            status: `Error occurred. Waiting 3 seconds before next call... (${i + 1}/${recordsToCall.length} completed)` 
          }));
          await new Promise(resolve => setTimeout(resolve, 3000)); // 3 second delay between calls
        }
      }
    }

    // Done
    setCallingProgress({
      current: recordsToCall.length,
      total: recordsToCall.length,
      status: 'All calls completed!',
      currentRecord: null
    });
    
    setIsBulkProcessing(false);
    loadTask(); // Refresh to get updated call statuses
    loadCredits(); // Refresh credits
    
    // Summary
    const successCount = results.filter(r => r.success).length;
    const failedCount = results.filter(r => !r.success).length;
    
    if (successCount > 0 || failedCount > 0) {
      alert(`Calling completed!\n✅ ${successCount} successful\n❌ ${failedCount} failed`);
    }
    
    // Clear selection after calling
    setSelectedRecords([]);
  };

  // View call details for a record
  const viewCallDetails = (record) => {
    setSelectedCallRecord(record);
    setCallDetails(null); // Reset call details
  };

  // Fetch call details when a record is selected
  useEffect(() => {
    const recordId = selectedCallRecord?.id;
    
    // Only fetch if record ID changed and record has call data
    if (recordId && recordId !== callRecordIdRef.current && selectedCallRecord?.called && id) {
      callRecordIdRef.current = recordId; // Update ref to prevent duplicate calls
      setLoadingCallDetails(true);
      setCallDetails(null); // Reset call details
      
      getCallDetails(id, recordId)
        .then(response => {
          if (response.success && response.data) {
            setCallDetails(response.data);
          } else {
            setError('Failed to load call details: ' + (response.message || 'Unknown error'));
          }
        })
        .catch(err => {
          console.error('Error loading call details:', err);
          setError('Failed to load call details: ' + err.message);
        })
        .finally(() => {
          setLoadingCallDetails(false);
        });
    } else if (!selectedCallRecord) {
      // Reset ref when modal is closed
      callRecordIdRef.current = null;
      setCallDetails(null);
    }
  }, [selectedCallRecord?.id, id, selectedCallRecord?.called]); // Only depend on record ID, not entire object

  const toggleRecordSelection = (recordId) => {
    setSelectedRecords(prev => {
      const isSelected = prev.includes(recordId);
      const newSelection = isSelected
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId];
      
      console.log(`📋 Record ${recordId} ${isSelected ? 'deselected' : 'selected'}. Total selected: ${newSelection.length}`);
      return newSelection;
    });
  };

  const toggleSelectAll = () => {
    const filteredRecords = getFilteredAndSortedRecords();
    const visibleRecordIds = filteredRecords.map(r => r.id);
    
    // Check if all visible records are already selected
    const allVisibleSelected = visibleRecordIds.length > 0 && 
                               visibleRecordIds.every(id => selectedRecords.includes(id));
    
    if (allVisibleSelected) {
      // Deselect all visible records (but keep selections from other pages)
      setSelectedRecords(prev => prev.filter(id => !visibleRecordIds.includes(id)));
      console.log(`📋 Deselected ${visibleRecordIds.length} visible records`);
    } else {
      // Select all visible records (merge with existing selections)
      setSelectedRecords(prev => {
        const newSelection = [...prev];
        visibleRecordIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        console.log(`📋 Selected ${visibleRecordIds.length} visible records. Total selected: ${newSelection.length}`);
        return newSelection;
      });
    }
  };

  // Filter and search records
  const getFilteredAndSortedRecords = () => {
    if (!task || !task.records) return [];
    
    let filtered = [...task.records];
    
    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(record => 
        (record.full_name && record.full_name.toLowerCase().includes(query)) ||
        (record.email && record.email.toLowerCase().includes(query)) ||
        (record.company_name && record.company_name.toLowerCase().includes(query)) ||
        (record.phone_number && record.phone_number.includes(query)) ||
        (record.linkedin_url && record.linkedin_url.toLowerCase().includes(query)) ||
        (record.company_website && record.company_website.toLowerCase().includes(query)) ||
        (record.state && record.state.toLowerCase().includes(query))
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(record => {
        switch (filterStatus) {
          case 'emailed':
            return record.emailed === true || record.email_status === 'sent';
          case 'not-emailed':
            return !record.emailed && record.email_status !== 'sent';
          case 'opened':
            return record.email_opened === true;
          case 'responded':
            return record.email_response && (record.email_response === 'yes' || record.email_response === 'no');
          case 'responded-yes':
            return record.email_response === 'yes';
          case 'responded-no':
            return record.email_response === 'no';
          case 'called':
            return record.called === true || record.call_status === 'success' || record.call_status === 'in_progress';
          case 'website-extracted':
            return record.website_extracted === true;
          case 'linkedin-scraped':
            return record.linkedin_scraped === true;
          default:
            return true;
        }
      });
    }
    
    // Apply verification filter
    if (filterVerification !== 'all') {
      filtered = filtered.filter(record => {
        const verificationStatus = record.manual_verification_status || 'pending';
        switch (filterVerification) {
          case 'verified':
            return verificationStatus === 'verified';
          case 'rejected':
            return verificationStatus === 'rejected';
          case 'pending':
            return verificationStatus === 'pending' || !verificationStatus;
          default:
            return true;
        }
      });
    }
    
    // Apply Email Stage filter
    if (filterEmailStage !== 'all') {
      filtered = filtered.filter(record => {
        switch (filterEmailStage) {
          case 'pass':
            return record.email_stage_status === 'pass';
          case 'reject':
            return record.email_stage_status === 'reject';
          case 'pending':
            return record.emailed && (!record.email_stage_status || record.email_stage_status === 'pending');
          case 'not-sent':
            return !record.emailed;
          default:
            return true;
        }
      });
    }
    
    // Apply Validation Stage filter
    if (filterValidationStage !== 'all') {
      filtered = filtered.filter(record => {
        switch (filterValidationStage) {
          case 'pass':
            return record.validation_stage_status === 'pass' || record.validation_status === 'qualified';
          case 'reject':
            return record.validation_stage_status === 'reject' || record.validation_status === 'rejected';
          case 'pending':
            return record.validation_status && record.validation_status !== 'qualified' && record.validation_status !== 'rejected';
          case 'not-validated':
            return !record.validation_status;
          default:
            return true;
        }
      });
    }
    
    // Apply Call Stage filter
    if (filterCallStage !== 'all') {
      filtered = filtered.filter(record => {
        switch (filterCallStage) {
          case 'pass':
            return record.call_stage_status === 'pass';
          case 'reject':
            return record.call_stage_status === 'reject';
          case 'pending':
            return record.called && (!record.call_stage_status || record.call_stage_status === 'pending');
          case 'not-called':
            return !record.called;
          default:
            return true;
        }
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = (a.full_name || '').toLowerCase();
          bValue = (b.full_name || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'company':
          aValue = (a.company_name || '').toLowerCase();
          bValue = (b.company_name || '').toLowerCase();
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
    
    return filtered;
  };
  
  // Pagination
  const getPaginatedRecords = () => {
    const filtered = getFilteredAndSortedRecords();
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filtered.slice(startIndex, endIndex);
  };
  
  const totalPages = Math.ceil((getFilteredAndSortedRecords().length || 0) / recordsPerPage);
  const totalFilteredRecords = getFilteredAndSortedRecords().length;
  const paginatedRecords = getPaginatedRecords();
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, filterVerification, filterEmailStage, filterValidationStage, filterCallStage, sortBy, sortOrder]);
  
  // Approval modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalRecordId, setApprovalRecordId] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'

  // Open approval modal
  const openApprovalModal = (recordId, action) => {
    setApprovalRecordId(recordId);
    setApprovalAction(action);
    setApprovalNotes('');
    setShowApprovalModal(true);
  };

  // Update manual verification status with notes
  const updateManualVerification = async (recordId, status, notes = '') => {
    try {
      const response = await updateTaskRecord(id, recordId, { 
        manual_verification_status: status,
        approval_notes: notes
      });
      if (response.success) {
        loadTask(); // Reload to show updated status
        setShowApprovalModal(false);
        setApprovalNotes('');
        setApprovalRecordId(null);
        setApprovalAction(null);
      } else {
        setError('Failed to update verification status: ' + response.message);
      }
    } catch (err) {
      setError('Failed to update verification status: ' + err.message);
    }
  };

  // Handle final approval/rejection
  const handleFinalApproval = async () => {
    if (!approvalRecordId || !approvalAction) return;
    
    const status = approvalAction === 'approve' ? 'verified' : 'rejected';
    await updateManualVerification(approvalRecordId, status, approvalNotes);
  };
  
  // Parse validation rules text to extract rule titles (same as displayed in modal)
  const parseValidationRuleTitles = (rulesText) => {
    if (!rulesText || !rulesText.trim()) return [];
    const instructionPatterns = [
      /^Make sure/i, /^anything that/i, /Evidence_Sources\s+(?:and|or)\s+Evidence_Sources/i,
      /you need to do web search/i, /so that it can be revalidate/i
    ];
    const lines = rulesText.split('\n');
    const ruleTitles = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (instructionPatterns.some(p => p.test(trimmed))) continue;
      // Split lines that contain " No " followed by lowercase (e.g. "X No tool-free numbers")
      const parts = trimmed.split(/\s+No\s+(?=[a-z])/);
      parts.forEach((part, i) => {
        const rule = (i > 0 ? 'No ' : '') + part.trim();
        if (rule && !instructionPatterns.some(p => p.test(rule))) {
          ruleTitles.push(rule);
        }
      });
    }
    return [...new Map(ruleTitles.map(r => [r, r])).values()]; // dedupe preserving order
  };

  // Get validation rule results from record (rules_details or qualification_reasons)
  const getValidationRuleResults = (record) => {
    try {
      const vr = record.validation_results;
      if (!vr) return [];
      const parsed = typeof vr === 'string' ? JSON.parse(vr) : vr;
      const step8 = parsed?.steps?.find(s => s.step === 8);
      const rules = step8?.data?.rules_details || step8?.data?.qualification_reasons 
        || parsed?.qualification_reasons || parsed?.summary?.qualification_reasons || [];
      return Array.isArray(rules) ? rules : [];
    } catch (e) {
      return [];
    }
  };

  // Match a rule title to validation result (fuzzy match by criterion)
  const matchRuleToResult = (ruleTitle, results) => {
    const rt = (ruleTitle || '').toLowerCase();
    const exact = results.find(r => 
      (r.criterion || '').toLowerCase() === rt || 
      (r.rule || '').toLowerCase() === rt
    );
    if (exact) return exact;
    // Contains match - rule title contained in criterion or vice versa
    const contains = results.find(r => {
      const c = (r.criterion || r.rule || '').toLowerCase();
      return c.includes(rt) || rt.includes(c) || 
        rt.split(/[\s:,]+/).slice(0,2).every(w => c.includes(w));
    });
    return contains || null;
  };

  // Build evidence sources and links from record
  const getEvidenceSourcesAndLinks = (record, sources = []) => {
    const srcArr = Array.isArray(sources) ? sources : [];
    const sourceNames = srcArr.map(s => String(s).replace(/_/g, ' '));
    const links = [];
    if (srcArr.some(s => /linkedin|profile/i.test(String(s))) && (record.linkedin_url || record.linkedin_url_contact)) {
      links.push(record.linkedin_url || record.linkedin_url_contact);
    }
    if (srcArr.some(s => /company|linkedin_company/i.test(String(s))) && record.linkedin_url_company) {
      links.push(record.linkedin_url_company);
    }
    if (srcArr.some(s => /website|website_data|company_website/i.test(String(s))) && (record.domain || record.company_website)) {
      const url = record.domain || record.company_website;
      links.push(url.startsWith('http') ? url : `https://${url}`);
    }
    return {
      sources: sourceNames.join('; ') || 'N/A',
      links: links.join(' | ') || 'N/A'
    };
  };

  // Download filtered records as Excel with all data + validation rule columns
  const downloadFilteredCSV = async () => {
    const filtered = getFilteredAndSortedRecords();
    if (filtered.length === 0) {
      setError('No records to download');
      return;
    }
    
    try {
      // Fetch enriched records with all related data
      const response = await getTaskRecordsForExport(id);
      if (!response.success || !response.data) {
        throw new Error('Failed to fetch records for export');
      }

      const enrichedRecords = response.data;
      const enrichedMap = new Map(enrichedRecords.map(r => [r.id, r]));

      // Parse validation rule titles from uploaded rules (same titles as modal)
      const rulesText = validationRules || task?.validation_rules || '';
      let validationRuleTitles = parseValidationRuleTitles(rulesText);

      // Fallback: if no rules from upload, use criterion from first validated record
      if (validationRuleTitles.length === 0) {
        for (const r of enrichedRecords) {
          const res = getValidationRuleResults(r);
          if (res.length > 0) {
            validationRuleTitles = res.map(x => x.criterion || x.rule || 'Unknown').filter(Boolean);
            break;
          }
        }
      }

      // Build validation column headers - same rule titles for every record
      const validationHeaders = [];
      validationRuleTitles.forEach(ruleTitle => {
        validationHeaders.push(`${ruleTitle} - Result`);
        validationHeaders.push(`${ruleTitle} - Description`);
        validationHeaders.push(`${ruleTitle} - Evidence_Sources`);
        validationHeaders.push(`${ruleTitle} - Evidence_Sources_Link`);
      });

      // Field mapping: original header -> db_field name for validation lookup
      // LinkedIn URL fields don't need validation columns - just show URL directly
      const FIELD_MAPPING = [
        { header: 'LinkedIn URL', dbField: 'linkedin_url_contact', skipValidation: true },
        { header: 'First Name', dbField: 'first_name', skipValidation: false },
        { header: 'Last Name', dbField: 'last_name', skipValidation: false },
        { header: 'Full Name', dbField: 'full_name', skipValidation: false },
        { header: 'Email', dbField: 'email', skipValidation: false },
        { header: 'Business Email', dbField: 'business_email', skipValidation: false },
        { header: 'Phone Number', dbField: 'phone_number', skipValidation: false },
        { header: 'Company Name', dbField: 'company_name', skipValidation: false },
        { header: 'Company Website', dbField: 'company_website', skipValidation: false },
        { header: 'Job Title', dbField: 'job_title', skipValidation: false },
        { header: 'Industry Type', dbField: 'industry_type', skipValidation: false },
        { header: 'Revenue Size', dbField: 'revenue_size', skipValidation: false },
        { header: 'Employee Size', dbField: 'employee_size', skipValidation: false },
        { header: 'Address Street', dbField: 'address_street', skipValidation: false },
        { header: 'City', dbField: 'city', skipValidation: false },
        { header: 'State', dbField: 'state', skipValidation: false },
        { header: 'Zip Code', dbField: 'zip_code', skipValidation: false },
        { header: 'Country', dbField: 'country', skipValidation: false },
        { header: 'LinkedIn URL Contact', dbField: 'linkedin_url_contact', skipValidation: true },
        { header: 'LinkedIn URL Company', dbField: 'linkedin_url_company', skipValidation: true },
        { header: 'Domain', dbField: 'domain', skipValidation: false }
      ];

      // Build headers: each original field followed by its 4 validation columns (skip for LinkedIn URLs)
      const headersWithValidation = FIELD_MAPPING.flatMap(({ header, dbField, skipValidation }) => {
        if (skipValidation) {
          // LinkedIn URLs: just show the field, no validation columns
          return [header];
        } else {
          // Other fields: show field + 4 validation columns
          return [
            header,
            `updated_${dbField}`,
            `${dbField}_evidence_link`,
            `${dbField}_evidence_source`,
            `${dbField}_final_status`
          ];
        }
      });

      // Excel headers - Original fields with validation columns interleaved + rest
      // NOTE: For the calling part we now only export a single transcription column.
      const headers = [
        // Original fields with validation columns immediately after each
        ...headersWithValidation,
        // Phone Update Info
        'Phone Number Updated', 'Phone Number History', 'Original Phone Number',
        // Validation Status
        'Validation Status', 'Validation Stage Status', 'Manual Verification Status',
        // Validation Rules (same titles as uploaded - proper align)
        ...validationHeaders,
        // Email Status & Response
        'Emailed', 'Email Status', 'Email Opened', 'Email Opened At', 
        'Email Response', 'Email Response At', 'Email Campaign ID', 'Email Sent At',
        // Call Status & Data
        'Called', 'Call Status', 'Call Log ID', 'Call Duration (seconds)', 
        'Call Recording URL', 'Call Created At',
        // Call Transcription (no AI scoring/verification/summary columns)
        'Call Transcription'
      ];
      
      // Convert records to CSV rows
      const rows = filtered.map(record => {
        const enriched = enrichedMap.get(record.id) || record;
        const updatedFieldsRaw = enriched.updated_fields || {};
        let updatedFields = { ...updatedFieldsRaw };
        // Derive Updated First/Last Name from Updated Full Name when missing
        if (updatedFields.full_name && (!updatedFields.first_name || !updatedFields.last_name)) {
          const parts = String(updatedFields.full_name).trim().split(/\s+/);
          if (parts.length >= 2) {
            if (!updatedFields.first_name) updatedFields.first_name = parts[0];
            if (!updatedFields.last_name) updatedFields.last_name = parts.slice(1).join(' ');
          }
        }
        const phoneHistory = enriched.phone_number_history || [];
        const phoneHistoryStr = phoneHistory.length > 0 
          ? JSON.stringify(phoneHistory).replace(/"/g, '""')
          : '';
        const originalPhone = phoneHistory.length > 0 && phoneHistory[0].from 
          ? phoneHistory[0].from 
          : record.phone_number || '';
        
        // Format call transcription - get from multiple sources
        // Use enriched data which has already extracted transcription properly
        let transcription = enriched.call_transcription || '';
        let fullTranscription = enriched.call_full_transcription || enriched.call_transcription || '';
        
        // If still no transcription, try to get from record directly
        if (!transcription && record.call_log_id) {
          // Transcription should be in enriched data, but fallback if needed
          transcription = '';
        }
        
        // Ensure we have the full transcription
        if (!fullTranscription && transcription) {
          fullTranscription = transcription;
        }
        if (!transcription && fullTranscription) {
          transcription = fullTranscription;
        }
        
        // Clean up transcription text (remove extra whitespace, ensure proper formatting)
        if (transcription) {
          transcription = transcription.trim().replace(/\n{3,}/g, '\n\n');
        }
        if (fullTranscription) {
          fullTranscription = fullTranscription.trim().replace(/\n{3,}/g, '\n\n');
        }

        // NOTE: AI verification / qualified lead values are intentionally not exported any more.
        // Build row: each original field value followed by its 4 validation values
        const rowDataWithValidation = FIELD_MAPPING.flatMap(({ header, dbField, skipValidation }) => {
          // Get original field value
          let originalValue = '';
          if (dbField === 'linkedin_url_contact' && header === 'LinkedIn URL') {
            originalValue = record.linkedin_url || record.linkedin_url_contact || '';
          } else if (dbField === 'linkedin_url_contact' && header === 'LinkedIn URL Contact') {
            originalValue = record.linkedin_url_contact || '';
          } else if (dbField === 'linkedin_url_company') {
            originalValue = record.linkedin_url_company || '';
          } else {
            originalValue = record[dbField] || '';
          }
          
          // LinkedIn URLs: just return the value, no validation columns
          if (skipValidation) {
            return [originalValue];
          }
          
          // Get validation details for this field
          const detail = (enriched.field_validation_details || []).find(d => d.db_field === dbField);
          const corrected = updatedFields[dbField] || detail?.corrected_value || '';
          
          // Determine updated value
          const updatedVal = corrected || '';
          
          // Determine final status: values match = CORRECT, values differ = UPDATED (already corrected)
          let finalStatus = '';
          const originalStr = String(originalValue || '').trim();
          const updatedStr = String(updatedVal || '').trim();
          
          if (originalStr && updatedStr && originalStr === updatedStr) {
            // Values match (e.g. Abhishek = Abhishek) → CORRECT
            finalStatus = 'CORRECT';
          } else if (updatedStr && updatedStr !== originalStr && originalStr !== '') {
            // Field was updated/corrected → show UPDATED (already fixed)
            finalStatus = 'UPDATED';
          } else if (!updatedStr || updatedStr === originalStr) {
            // No update or same value → CORRECT
            finalStatus = 'CORRECT';
          } else if (detail && detail.status) {
            finalStatus = detail.status;
          }
          
          // Get evidence link and source from detail; fallback to record URLs when empty
          let evidenceLink = detail?.evidence_link || '';
          let evidenceSource = detail?.evidence_source || '';
          
          // Fallback: use record URLs when validation has data but evidence is empty
          if ((updatedVal || finalStatus) && !evidenceLink) {
            const fallbackLinks = [];
            const personFields = ['full_name', 'first_name', 'last_name', 'email', 'business_email', 'phone_number', 'job_title'];
            const companyFields = ['company_name', 'company_website', 'industry_type', 'employee_size', 'domain'];
            if (personFields.includes(dbField) && (record.linkedin_url_contact || record.linkedin_url)) {
              fallbackLinks.push(record.linkedin_url_contact || record.linkedin_url);
            }
            if (companyFields.includes(dbField) && record.linkedin_url_company) {
              fallbackLinks.push(record.linkedin_url_company);
            }
            if (['company_name', 'company_website', 'domain', 'email'].includes(dbField) && (record.company_website || record.domain)) {
              const url = record.company_website || record.domain;
              fallbackLinks.push(url.startsWith('http') ? url : `https://${url}`);
            }
            evidenceLink = fallbackLinks.join(' | ');
          }
          if ((updatedVal || finalStatus) && !evidenceSource) {
            const sources = [];
            if (evidenceLink && evidenceLink.includes('linkedin.com/in')) sources.push('linkedin_profile');
            if (evidenceLink && evidenceLink.includes('linkedin.com/company')) sources.push('linkedin_company');
            if (evidenceLink && !evidenceLink.includes('linkedin.com')) sources.push('company_website');
            evidenceSource = sources.join(', ') || 'record_data';
          }
          
          return [
            originalValue,
            (updatedVal || '').toString().replace(/"/g, '""'),
            evidenceLink.toString().replace(/"/g, '""'),
            evidenceSource.toString().replace(/"/g, '""'),
            finalStatus
          ];
        });

        return [
          // Original fields with validation columns immediately after each
          ...rowDataWithValidation,
          // Phone update info
          record.phone_number_updated ? 'Yes' : 'No',
          phoneHistoryStr,
          originalPhone,
          // Validation status
          record.validation_status || '',
          record.validation_stage_status || '',
          record.manual_verification_status || 'pending',
          // Validation Rules - same titles per record, proper align
          ...validationRuleTitles.flatMap(ruleTitle => {
            const results = getValidationRuleResults(enriched);
            const matched = matchRuleToResult(ruleTitle, results);
            const recordData = { ...record, ...enriched };
            const evidence = matched ? getEvidenceSourcesAndLinks(recordData, matched.sources) : { sources: 'N/A', links: 'N/A' };
            return [
              matched ? (matched.passed ? 'PASS' : 'FAIL') : '',
              matched ? (matched.reason || '') : '',
              evidence.sources,
              evidence.links
            ];
          }),
          // Email status
          record.emailed ? 'Yes' : 'No',
          record.email_status || '',
          record.email_opened ? 'Yes' : 'No',
          record.email_opened_at || '',
          record.email_response || '',
          record.email_response_at || '',
          record.email_campaign_id || '',
          record.email_sent_at || '',
          // Call status
          record.called ? 'Yes' : 'No',
          record.call_status || '',
          enriched.call_log_id || '',
          enriched.call_duration || '',
          enriched.call_recording_url || '',
          enriched.call_created_at || '',
          // Call transcription (single column in Excel; use best available text)
          fullTranscription || transcription || ''
        ];
      });
      
      // Create Excel file using XML format (Excel-compatible)
      const createExcelFile = (headers, rows) => {
        // Excel XML format
        const xmlHeader = '<?xml version="1.0"?>\n<?mso-application progid="Excel.Sheet"?>\n<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n xmlns:o="urn:schemas-microsoft-com:office:office"\n xmlns:x="urn:schemas-microsoft-com:office:excel"\n xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"\n xmlns:html="http://www.w3.org/TR/REC-html40">\n<Worksheet ss:Name="Leads">\n<Table>';
        
        // Escape XML
        const escapeXML = (value) => {
          if (value === null || value === undefined) return '';
          const str = String(value);
          return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
        };
        
        // Create header row
        const headerRow = '<Row>' + headers.map(h => `<Cell><Data ss:Type="String">${escapeXML(h)}</Data></Cell>`).join('') + '</Row>';
        
        // Create data rows
        const dataRows = rows.map(row => {
          const cells = row.map(cell => {
            const cellValue = escapeXML(cell);
            // Use String type for all cells to preserve formatting
            return `<Cell><Data ss:Type="String">${cellValue}</Data></Cell>`;
          });
          return '<Row>' + cells.join('') + '</Row>';
        }).join('\n');
        
        const xmlFooter = '</Table>\n</Worksheet>\n</Workbook>';
        
        return xmlHeader + '\n' + headerRow + '\n' + dataRows + '\n' + xmlFooter;
      };
      
      // Generate Excel file
      const excelContent = createExcelFile(headers, rows);
      
      // Create download link
      const blob = new Blob([excelContent], { 
        type: 'application/vnd.ms-excel' 
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `leads_${task.task_name}_${new Date().toISOString().split('T')[0]}.xls`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error('Error downloading CSV:', err);
      setError('Failed to download CSV: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading task...</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <p className="text-gray-400">Task not found</p>
          <button
            onClick={() => navigate('/tasks')}
            className="mt-4 text-blue-400 hover:text-blue-300"
          >
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex gap-4">
          {/* Side Panel - Upload and Download Options (Collapsible) */}
          {sidebarOpen && (
            <div className="w-64 flex-shrink-0 transition-all duration-300">
              <div className="bg-[#1a1f3a]/80 backdrop-blur-sm rounded-xl shadow-xl p-4 border border-blue-500/30 sticky top-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-white">Quick Actions</h3>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="text-gray-400 hover:text-gray-300 transition-colors"
                    title="Close sidebar"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-2.5">
                  {/* Upload Document Button */}
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white px-3 py-2.5 rounded-lg text-sm font-semibold hover:from-blue-500 hover:to-blue-400 shadow-lg shadow-blue-500/25 transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload Document
                  </button>

                  {/* Download Sample CSV Button */}
                  <button
                    onClick={downloadSampleCSV}
                    className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white px-3 py-2.5 rounded-lg text-sm font-semibold hover:from-green-500 hover:to-green-400 shadow-lg shadow-green-500/25 transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Sample CSV
                  </button>
                </div>

                {/* Field Names Info - Compact */}
                <div className="mt-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                  <h4 className="text-xs font-semibold text-gray-300 mb-2">CSV Headers:</h4>
                  <div className="text-[10px] text-gray-400 leading-tight max-h-48 overflow-y-auto">
                    <div className="break-words">First Name, Last Name, Business Email, domain, Job Title, Company Name, Industry Type, Revenue Size, Employee Size, Phone Number, Address Street, City, State, Zip Code, Country, Linkedin URL For Contact, Linkedin URL For Company</div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2 pt-2 border-t border-slate-700">
                    Download sample CSV for format reference
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Toggle Button - Show when sidebar is closed */}
          {!sidebarOpen && (
            <div className="flex-shrink-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="bg-[#1a1f3a]/80 backdrop-blur-sm rounded-lg shadow-lg p-3 border border-blue-500/30 hover:shadow-xl transition-all sticky top-8"
                title="Open sidebar"
              >
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="mb-6 bg-[#1a1f3a]/80 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-blue-500/30">
          <div className="flex items-start justify-between gap-6">
            {/* Left Section - Back button and Title */}
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate('/tasks')}
                className="text-blue-400 hover:text-blue-300 mb-3 text-sm font-medium flex items-center gap-1 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Tasks
              </button>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-white mb-1">{task.task_name}</h1>
                <button
                  onClick={() => {
                    setEditTaskData({
                      task_name: task.task_name || '',
                      description: task.description || '',
                      calling_by: task.calling_by || '',
                      agent_name: task.agent_name || '',
                      retell_from_number: task.retell_from_number || '',
                      script: task.script || '',
                      caller_company: task.caller_company || 'The Tech Marketer',
                      assets: task.assets || 'Signal-First Demand Generation content from Market Wavegen'
                    });
                    setShowEditTaskModal(true);
                  }}
                  className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"
                  title="Edit Task"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </div>
              {task.description && (
                <p className="text-gray-400 text-sm">{task.description}</p>
              )}
              {/* Record Count */}
              {task.records && (
                <div className="mt-3 flex items-center gap-4 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="font-semibold text-white">{task.records.length.toLocaleString()}</span> records
                  </span>
                  {task.records.filter(r => r.emailed).length > 0 && (
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-semibold text-white">{task.records.filter(r => r.emailed).length.toLocaleString()}</span> emailed
                    </span>
                  )}
                  {task.records.filter(r => r.email_response === 'yes').length > 0 && (
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-semibold text-white">{task.records.filter(r => r.email_response === 'yes').length.toLocaleString()}</span> responded
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Right Section - Dual Credits */}
            <div className="flex items-center gap-4">
              {credits !== null && (
                <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-lg px-4 py-3 border border-purple-500/30 shadow-lg backdrop-blur-sm">
                  <div className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-1">Call Credits</div>
                  <div className={`text-3xl font-bold ${credits > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {credits}
                  </div>
                </div>
              )}
              {validationCredits !== null && (
                <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 rounded-lg px-4 py-3 border border-emerald-500/30 shadow-lg backdrop-blur-sm">
                  <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-1">Validation Credits</div>
                  <div className={`text-3xl font-bold ${validationCredits > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {validationCredits}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ============ VALIDATION PROGRESS PANEL ============ */}
        {isValidating && (
          <ValidationProgressPanel 
            taskId={id} 
            isProcessing={isValidating}
            onRestart={handleRestartValidation}
            initialRunTotal={validationRunTotal}
          />
        )}

        {/* ============ TOP-LEVEL OPERATIONS BAR ============ */}
        <div className="mb-6 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-xl shadow-xl p-4 border border-slate-700">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-white font-semibold text-sm uppercase tracking-wider">Operations</span>
              {isBulkProcessing && !isValidating && (
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium flex items-center gap-2">
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="animate-pulse">{bulkProgress.status || 'Processing...'}</span>
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {/* Validation Operation - now shown FIRST in the operations row */}
              <button
                onClick={async () => {
                  setShowValidationModal(true);
                  // Load validation rules when modal opens
                  await loadValidationRules();
                }}
                disabled={isBulkProcessing || !task?.records?.length || (validationCredits !== null && validationCredits <= 0)}
                className="group relative px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-lg font-semibold text-sm hover:from-emerald-500 hover:to-emerald-400 transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Validations
              </button>

              {/* Email Operation - now shown SECOND in the operations row */}
              <button
                onClick={handleBulkEmailStart}
                disabled={isBulkProcessing || !task?.records?.length}
                className="group relative px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg font-semibold text-sm hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Email
              </button>

              {/* Calling Agent Operation */}
              <button
                onClick={() => setShowCallingModal(true)}
                disabled={isBulkProcessing || !task?.records?.length || (credits !== null && credits <= 0)}
                className="group relative px-5 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg font-semibold text-sm hover:from-purple-500 hover:to-purple-400 transition-all shadow-lg shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Calling Agent
              </button>

              {/* Master Operation */}
              <button
                onClick={() => setShowMasterQueueModal(true)}
                disabled={isBulkProcessing || !task?.records?.length}
                className="group relative px-6 py-2.5 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white rounded-lg font-bold text-sm hover:from-amber-400 hover:via-orange-400 hover:to-red-400 transition-all shadow-lg shadow-orange-500/25 disabled:opacity-50 disabled:cursor-not-allowed border border-amber-400/30"
              >
                MASTER
              </button>
            </div>
          </div>
        </div>
        {/* ============ END TOP-LEVEL OPERATIONS BAR ============ */}

        {error && (
          <div className="mb-4 p-4 bg-red-900/30 border-2 border-red-500/50 rounded-lg shadow-lg backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-300 font-medium">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-300"
                title="Dismiss error"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Bulk Operations */}
        {task.records && task.records.length > 0 && selectedRecords.length > 0 && (
          <div className="mb-4 p-4 bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-2 border-blue-500/50 rounded-lg shadow-xl backdrop-blur-sm">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                  {selectedRecords.length}
                </div>
                <div>
                  <span className="text-blue-300 font-semibold text-lg">
                    {selectedRecords.length.toLocaleString()} record(s) selected
                  </span>
                  <p className="text-xs text-blue-400 mt-0.5">
                    {selectedRecords.length === totalFilteredRecords 
                      ? 'All visible records selected' 
                      : `${selectedRecords.length.toLocaleString()} of ${totalFilteredRecords.toLocaleString()} visible records selected`}
                  </p>
                </div>
              </div>
              {/* Commented out: Bulk operation buttons (Extract Websites, Make Calls, Send Emails, Clear Selection) */}
              {/* <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleBulkOperation('extract-website')}
                  disabled={operationLoading.bulk === 'extract-website'}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                >
                  {operationLoading.bulk === 'extract-website' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Extracting...
                    </>
                  ) : (
                    <>
                      <span>🌐</span>
                      Extract Websites
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleBulkOperation('call')}
                  disabled={operationLoading.bulk === 'call' || (credits !== null && credits < selectedRecords.length)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                  title={
                    credits !== null && credits < selectedRecords.length 
                      ? `Insufficient credits. You have ${credits} but need ${selectedRecords.length}` 
                      : operationLoading.bulk === 'call'
                      ? 'Calls are being processed sequentially (one at a time). This may take several minutes.'
                      : `Make calls to ${selectedRecords.length} selected record(s). Calls will be processed sequentially.`
                  }
                >
                  {operationLoading.bulk === 'call' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing Calls...
                    </>
                  ) : (
                    <>
                      <span>📞</span>
                      Make Calls ({selectedRecords.length})
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleBulkOperation('send-email')}
                  disabled={operationLoading.bulk === 'send-email'}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                >
                  {operationLoading.bulk === 'send-email' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <span>📧</span>
                      Send Emails
                    </>
                  )}
                </button>
                <button
                  onClick={() => setSelectedRecords([])}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg text-sm font-semibold hover:bg-gray-600 shadow-md hover:shadow-lg transition-all"
                >
                  Clear Selection
                </button>
              </div> */}
            </div>
          </div>
        )}
        
        {/* Bulk Email Campaign Selection Modal */}
        {showBulkEmailModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl relative z-[10000]">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">📧 Send Bulk Emails</h2>
              <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                <p className="text-sm font-semibold text-blue-900 mb-2">
                  Sending emails to {selectedRecords.length} selected record(s)
                </p>
                <p className="text-xs text-blue-700">
                  Each record will receive an email using the selected campaign template.
                </p>
              </div>
              <p className="text-sm text-gray-700 mb-4 font-medium">
                Select a campaign template:
              </p>

              {loadingCampaigns ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading campaigns...</p>
                </div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📭</div>
                  <p className="text-gray-600 mb-2 font-semibold">No campaigns found</p>
                  <p className="text-sm text-gray-500 mb-4">Create a campaign template first</p>
                  <button
                    onClick={() => {
                      setShowBulkEmailModal(false);
                      window.open('/campaigns/create?fromTask=true', '_blank');
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700"
                  >
                    Create Campaign Template
                  </button>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-2">
                    {campaigns.map((campaign) => (
                      <button
                        key={campaign.id}
                        onClick={() => handleBulkEmailSend(campaign.id)}
                        className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 text-lg">{campaign.campaign_name}</h3>
                            <p className="text-sm text-gray-700 mt-1 font-medium">Subject: {campaign.subject_line}</p>
                            <div className="flex items-center space-x-4 mt-3 text-xs">
                              <span className="px-2 py-1 bg-gray-100 rounded">Total: {campaign.total_recipients || 0}</span>
                              <span className="px-2 py-1 bg-gray-100 rounded">Sent: {campaign.sent_count || 0}</span>
                              <span className={`px-2 py-1 rounded font-medium ${
                                campaign.status === 'completed' ? 'bg-green-100 text-green-800' :
                                campaign.status === 'sending' ? 'bg-yellow-100 text-yellow-800' :
                                campaign.status === 'failed' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {campaign.status || 'draft'}
                              </span>
                            </div>
                            <p className="text-xs text-blue-600 mt-2 font-medium">Click to send emails using this campaign →</p>
                          </div>
                          <div className="ml-4 flex items-center">
                            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowBulkEmailModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Compact Search and Filter Bar - Dashboard Style */}
        {task.records && task.records.length > 0 && (
          <div className="mb-4 bg-[#1a1f3a]/80 backdrop-blur-sm rounded-lg shadow-lg p-3 border border-blue-500/30">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search - Compact */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search records..."
                    className="w-full pl-10 pr-8 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                      title="Clear search"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Status Filter - Compact */}
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-sm text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                <option value="all">All Status</option>
                  <option value="emailed">📧 Emailed</option>
                  <option value="not-emailed">📧 Not Emailed</option>
                <option value="opened">👁️ Opened</option>
                  <option value="responded">💬 Responded</option>
                  <option value="called">📞 Called</option>
                </select>
              
              {/* Verification Filter - Compact */}
              <select
                value={filterVerification}
                onChange={(e) => setFilterVerification(e.target.value)}
                className="px-3 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-sm text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="all">All Verification</option>
                <option value="verified">✅ Verified</option>
                <option value="rejected">❌ Rejected</option>
                <option value="pending">⏳ Pending</option>
              </select>
              
              {/* Validation Stage Filter - now shown BEFORE email to match button order */}
              <select
                value={filterValidationStage}
                onChange={(e) => setFilterValidationStage(e.target.value)}
                className="px-3 py-2 bg-green-900/30 border border-green-500/40 rounded-lg text-sm text-green-200 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                title="Filter by Validation Status"
              >
                <option value="all">✅ All Validation</option>
                <option value="pass">✅ Valid Pass</option>
                <option value="reject">❌ Valid Reject</option>
                <option value="pending">⏳ Valid Pending</option>
                <option value="not-validated">📋 Not Validated</option>
              </select>

              {/* Email Stage Filter - now shown AFTER validation to match button order */}
              <select
                value={filterEmailStage}
                onChange={(e) => setFilterEmailStage(e.target.value)}
                className="px-3 py-2 bg-blue-900/30 border border-blue-500/40 rounded-lg text-sm text-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                title="Filter by Email Response Status"
              >
                <option value="all">📧 All Email</option>
                <option value="pass">✅ Email Pass (Yes)</option>
                <option value="reject">❌ Email Reject (No)</option>
                <option value="pending">⏳ Email Pending</option>
                <option value="not-sent">📭 Not Sent</option>
              </select>
              
              {/* Call Stage Filter */}
              <select
                value={filterCallStage}
                onChange={(e) => setFilterCallStage(e.target.value)}
                className="px-3 py-2 bg-purple-900/30 border border-purple-500/40 rounded-lg text-sm text-purple-200 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                title="Filter by Call Status"
              >
                <option value="all">📞 All Calls</option>
                <option value="pass">✅ Call Pass</option>
                <option value="reject">❌ Call Reject</option>
                <option value="pending">⏳ Call Pending</option>
                <option value="not-called">📵 Not Called</option>
              </select>
              
              {/* Sort - Compact */}
              <div className="flex gap-1">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  className="px-2 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-xs text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                  <option value="created_at">Date</option>
                  <option value="name">Name</option>
                    <option value="email">Email</option>
                    <option value="company">Company</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-2 py-2 bg-slate-800/50 border border-slate-600 rounded-lg hover:bg-slate-700/50 focus:ring-2 focus:ring-blue-500 transition-all text-xs font-semibold text-gray-200"
                    title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
            </div>
            
              {/* Download Button */}
                  <button
                onClick={downloadFilteredCSV}
                className="px-3 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg text-sm font-semibold hover:from-green-500 hover:to-green-400 shadow-lg shadow-green-500/25 transition-all flex items-center gap-2"
                title="Download filtered records as CSV"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                Download
                  </button>
              
              {/* Records Per Page - Compact */}
                <select
                  value={recordsPerPage}
                  onChange={(e) => {
                    setRecordsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                className="px-2 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-xs text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value={25}>25/page</option>
                <option value={50}>50/page</option>
                <option value={100}>100/page</option>
                <option value={200}>200/page</option>
                <option value={500}>500/page</option>
                </select>
              </div>
            
            {/* Results Summary - Compact */}
            <div className="mt-2 pt-2 border-t border-slate-700 flex items-center justify-between text-xs text-gray-400">
              <div className="flex items-center gap-2">
                <span>Showing <span className="font-bold text-white">{paginatedRecords.length.toLocaleString()}</span> of <span className="font-bold text-white">{totalFilteredRecords.toLocaleString()}</span> records</span>
                {(searchQuery || filterStatus !== 'all' || filterVerification !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilterStatus('all');
                      setFilterVerification('all');
                    }}
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              {totalFilteredRecords < task.records.length && (
                <span className="text-gray-500">
                  (filtered from <span className="font-semibold text-gray-300">{task.records.length.toLocaleString()}</span> total)
                </span>
              )}
            </div>
          </div>
        )}

        {/* Records Table */}
        {!task.records || task.records.length === 0 ? (
          <div className="bg-[#1a1f3a]/80 backdrop-blur-sm rounded-xl shadow-xl p-8 text-center border border-blue-500/30">
            <div className="text-4xl mb-4">📊</div>
            <p className="text-gray-300 mb-2">No records found</p>
            <p className="text-sm text-gray-400 mb-4">Upload records to get started</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:from-blue-500 hover:to-blue-400 shadow-lg shadow-blue-500/25 transition-all"
            >
              Upload Records
            </button>
          </div>
        ) : (
          <div className="bg-[#1a1f3a]/80 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden border border-blue-500/30">
            <div className="overflow-x-auto max-h-[calc(100vh-300px)]">
              <table className="min-w-full divide-y divide-slate-700" style={{ minWidth: '2000px' }}>
                <thead className="bg-gradient-to-r from-slate-800/50 via-slate-700/50 to-slate-800/50">
                  <tr>
                    <th className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={selectedRecords.length === paginatedRecords.length && paginatedRecords.length > 0 && paginatedRecords.every(r => selectedRecords.includes(r.id))}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-2 cursor-pointer"
                        title="Select all visible records"
                      />
                    </th>
                    {/* Serial number column for clearer indexing */}
                    <th className="px-2 py-2 text-center text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">
                      S.No
                    </th>
                    {/* Data Columns - All CSV Fields */}
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">First Name</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Last Name</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Business Email</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Domain</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Job Title</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Company</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Industry</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Revenue</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Employees</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Phone</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Address</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">City</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">State</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Zip</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">Country</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">LinkedIn Contact</th>
                    <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">LinkedIn Company</th>
                    {/* Stage Status Columns */}
                    {/* Validation icon first, then Email to match top buttons */}
                    <th className="px-2 py-2 text-center text-[10px] font-bold text-green-300 uppercase tracking-wider whitespace-nowrap bg-green-900/20">✅</th>
                    <th className="px-2 py-2 text-center text-[10px] font-bold text-blue-300 uppercase tracking-wider whitespace-nowrap bg-blue-900/20">📧</th>
                    <th className="px-2 py-2 text-center text-[10px] font-bold text-purple-300 uppercase tracking-wider whitespace-nowrap bg-purple-900/20">📞</th>
                    <th className="px-2 py-2 text-center text-[10px] font-bold text-yellow-300 uppercase tracking-wider whitespace-nowrap bg-yellow-900/20">👤</th>
                    <th className="px-2 py-2 text-center text-[10px] font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap sticky right-0 bg-slate-800">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-slate-900/30 divide-y divide-slate-700">
                  {paginatedRecords.length === 0 ? (
                    <tr>
                      {/* +1 colSpan for new S.No column */}
                      <td colSpan="24" className="px-6 py-16 text-center">
                        <div className="text-gray-300">
                          {searchQuery || filterStatus !== 'all' ? (
                            <>
                              <div className="text-6xl mb-4">🔍</div>
                              <p className="text-lg font-medium text-gray-200 mb-2">No records found matching your criteria</p>
                              <p className="text-sm text-gray-400 mb-4">Try adjusting your search or filter settings</p>
                              <button
                                onClick={() => {
                                  setSearchQuery('');
                                  setFilterStatus('all');
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg font-semibold hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/25"
                              >
                                Clear All Filters
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="text-6xl mb-4">📊</div>
                              <p className="text-lg font-medium text-gray-200 mb-2">No records found</p>
                              <p className="text-sm text-gray-400 mb-4">Upload records to get started</p>
                              <button
                                onClick={() => setShowUploadModal(true)}
                                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg font-semibold hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/25"
                              >
                                Upload Records
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedRecords.map((record, index) => {
                      const isExpanded = expandedRows.has(record.id);
                      // Get scraping result from state or from record data
                      let scrapingResult = websiteScrapingResults[record.id];
                      if (!scrapingResult && record.website_extraction_data) {
                        try {
                          scrapingResult = typeof record.website_extraction_data === 'string' 
                            ? JSON.parse(record.website_extraction_data)
                            : record.website_extraction_data;
                        } catch (e) {
                          console.error('Error parsing website extraction data:', e);
                        }
                      }
                      // 1-based serial number across all pages
                      const serialNumber = (currentPage - 1) * recordsPerPage + index + 1;
                      
                      // Parse LinkedIn data
                      let linkedinProfileData = null;
                      let linkedinCompanyData = null;
                      let validationResults = null;
                      
                      if (record.linkedin_profile_data) {
                        try {
                          linkedinProfileData = typeof record.linkedin_profile_data === 'string'
                            ? JSON.parse(record.linkedin_profile_data)
                            : record.linkedin_profile_data;
                        } catch (e) {
                          console.error('Error parsing LinkedIn profile data:', e);
                        }
                      }
                      
                      if (record.linkedin_company_data) {
                        try {
                          linkedinCompanyData = typeof record.linkedin_company_data === 'string'
                            ? JSON.parse(record.linkedin_company_data)
                            : record.linkedin_company_data;
                        } catch (e) {
                          console.error('Error parsing LinkedIn company data:', e);
                        }
                      }
                      
                      if (record.validation_results) {
                        try {
                          validationResults = typeof record.validation_results === 'string'
                            ? JSON.parse(record.validation_results)
                            : record.validation_results;
                        } catch (e) {
                          console.error('Error parsing validation results:', e);
                        }
                      }
                      
                      // Check if lead is FINALLY rejected (manual verification only, not 3-stage rejections)
                      const isFinallyRejected = record.manual_verification_status === 'rejected';
                      
                      return (
                        <React.Fragment key={record.id}>
                          <tr 
                            className={`transition-all duration-150 cursor-pointer ${
                              isFinallyRejected 
                                ? 'border-2 border-red-500 bg-red-900/40 hover:bg-red-900/50 shadow-lg shadow-red-500/30 ring-2 ring-red-500/50' 
                                : 'hover:bg-slate-800/50 border-l-4 border-transparent'
                            } ${selectedRecords.includes(record.id) && !isFinallyRejected ? 'bg-indigo-900/30' : ''} ${!isFinallyRejected && index % 2 === 0 ? 'bg-slate-900/20' : ''} ${!isFinallyRejected && index % 2 === 1 ? 'bg-slate-900/30' : ''}`}
                            onClick={() => {
                              // If record has validation data, open modal; otherwise expand row
                              if (record.validation_status || record.validation_results || record.linkedin_profile_data || record.linkedin_company_data) {
                                setSelectedValidationRecord(record);
                                setShowValidationDetailModal(true);
                              } else {
                                toggleRowExpansion(record.id);
                              }
                            }}
                          >
                            <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedRecords.includes(record.id)}
                                onChange={() => toggleRecordSelection(record.id)}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-2 cursor-pointer"
                                title="Select this record"
                              />
                            </td>
                            {/* Serial number cell */}
                            <td className="px-2 py-2 text-center text-[11px] text-gray-400">
                              {serialNumber}
                            </td>
                          {/* First Name */}
                          <td className="px-2 py-2 text-[11px] text-gray-200">
                            <div className="truncate max-w-[70px]" title={record.first_name || '-'}>
                              {record.first_name || '-'}
                            </div>
                          </td>
                          {/* Last Name */}
                          <td className="px-2 py-2 text-[11px] text-gray-200">
                            <div className="truncate max-w-[70px]" title={record.last_name || '-'}>
                              {record.last_name || '-'}
                            </div>
                          </td>
                          {/* Business Email */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[100px]" title={record.business_email || record.email || '-'}>
                              {record.business_email || record.email || '-'}
                            </div>
                          </td>
                          {/* Domain */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[70px]" title={record.domain || '-'}>
                              {record.domain || '-'}
                            </div>
                          </td>
                          {/* Job Title */}
                          <td className="px-2 py-2 text-[11px] text-gray-200">
                            <div className="truncate max-w-[80px]" title={record.job_title || '-'}>
                              {record.job_title || '-'}
                            </div>
                          </td>
                          {/* Company Name */}
                          <td className="px-2 py-2 text-[11px] text-gray-200 font-medium">
                            <div className="truncate max-w-[80px]" title={record.company_name || '-'}>
                              {record.company_name || '-'}
                            </div>
                          </td>
                          {/* Industry Type */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[70px]" title={record.industry_type || '-'}>
                              {record.industry_type || '-'}
                            </div>
                          </td>
                          {/* Revenue Size */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[60px]" title={record.revenue_size || '-'}>
                              {record.revenue_size || '-'}
                            </div>
                          </td>
                          {/* Employee Size */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[50px]" title={record.employee_size || '-'}>
                              {record.employee_size || '-'}
                            </div>
                          </td>
                          {/* Phone Number */}
                          <td className="px-2 py-2 text-[11px] text-gray-300 font-mono">
                            <div className="truncate max-w-[90px]" title={record.phone_number || '-'}>
                              {record.phone_number || '-'}
                            </div>
                          </td>
                          {/* Address Street */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[100px]" title={record.address_street || '-'}>
                              {record.address_street || '-'}
                            </div>
                          </td>
                          {/* City */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[60px]" title={record.city || '-'}>
                              {record.city || '-'}
                            </div>
                          </td>
                          {/* State */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[60px]" title={record.state || '-'}>
                              {record.state || '-'}
                            </div>
                          </td>
                          {/* Zip Code */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[50px]" title={record.zip_code || '-'}>
                              {record.zip_code || '-'}
                            </div>
                          </td>
                          {/* Country */}
                          <td className="px-2 py-2 text-[11px] text-gray-300">
                            <div className="truncate max-w-[60px]" title={record.country || '-'}>
                              {record.country || '-'}
                            </div>
                          </td>
                          {/* LinkedIn URL Contact */}
                          <td className="px-2 py-2 text-[11px]">
                            {(record.linkedin_url_contact || record.linkedin_url) ? (
                              <a href={record.linkedin_url_contact || record.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline truncate max-w-[80px] block" onClick={(e) => e.stopPropagation()}>
                                View
                                </a>
                              ) : (
                              <span className="text-gray-500">-</span>
                              )}
                          </td>
                          {/* LinkedIn URL Company */}
                          <td className="px-2 py-2 text-[11px]">
                            {record.linkedin_url_company ? (
                              <a href={record.linkedin_url_company} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline truncate max-w-[80px] block" onClick={(e) => e.stopPropagation()}>
                                View
                              </a>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          
                          {/* VALIDATION STAGE STATUS - Click to view details (now appears BEFORE email in table) */}
                          <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => {
                                if (record.validation_status || record.validation_stage_status) {
                                  setSelectedValidationRecord(record);
                                  setShowValidationDetailModal(true);
                                }
                              }}
                              disabled={!record.validation_status && !record.validation_stage_status}
                              className={`${(record.validation_status || record.validation_stage_status) ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
                              title={record.validation_status || record.validation_stage_status ? 'Click to view validation details' : 'Validation: Not started'}
                            >
                            {(record.validation_stage_status === 'pass' || record.validation_status === 'qualified') ? (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-900/50 border border-green-500/50" title="Validation: QUALIFIED - Click for details">
                                <span className="text-green-400 text-xs">✓</span>
                                    </span>
                            ) : (record.validation_stage_status === 'reject' || record.validation_status === 'rejected') ? (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-900/50 border border-red-500/50" title="Validation: REJECTED - Click for details">
                                <span className="text-red-400 text-xs">✗</span>
                                </span>
                            ) : (record.validation_status === 'pending' || record.validation_status === 'processing' || record.validation_status === 'needs_review') ? (
                              <ValidationProgressIndicator record={record} taskId={id} />
                            ) : (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-800/50 border border-gray-600/50" title="Validation: Not started">
                                <span className="text-gray-500 text-[10px]">-</span>
                                </span>
                              )}
                            </button>
                          </td>

                          {/* EMAIL STAGE STATUS - Click to view details (now appears AFTER validation in table) */}
                          <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => {
                                if (record.emailed || record.email_response || record.email_stage_status) {
                                  setSelectedEmailRecord(record);
                                  setShowEmailResponseModal(true);
                                }
                              }}
                              disabled={!record.emailed && !record.email_response && !record.email_stage_status}
                              className={`${(record.emailed || record.email_response || record.email_stage_status) ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
                              title={record.emailed || record.email_response || record.email_stage_status ? 'Click to view email details' : 'Email: Not sent'}
                            >
                            {record.email_stage_status === 'pass' ? (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-900/50 border border-green-500/50" title="Email: PASS (Yes response)">
                                <span className="text-green-400 text-xs">✓</span>
                                  </span>
                            ) : record.email_stage_status === 'reject' ? (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-900/50 border border-red-500/50" title="Email: REJECT (No response)">
                                <span className="text-red-400 text-xs">✗</span>
                                </span>
                            ) : record.emailed ? (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-900/50 border border-yellow-500/50" title="Email: Sent, awaiting response">
                                <span className="text-yellow-400 text-[10px]">⏳</span>
                                    </span>
                                  ) : (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-800/50 border border-gray-600/50" title="Email: Not sent">
                                <span className="text-gray-500 text-[10px]">-</span>
                                    </span>
                                  )}
                            </button>
                          </td>
                          
                          {/* CALL STAGE STATUS - Click to view details */}
                          <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <button
                              onClick={() => record.called && setSelectedCallRecord(record)}
                              disabled={!record.called}
                              className={`${record.called ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
                              title={record.called ? 'Click to view call details' : 'Call: Not made'}
                            >
                              {/*
                                Simplified call status:
                                - Green tick = call done (any final outcome)
                                - Yellow pending icon = call in progress / initiated but not completed
                                - Grey dash = call not started
                              */}
                              {record.called && (record.call_stage_status === 'pass' || record.call_stage_status === 'reject' || record.call_status === 'completed' || record.call_status === 'success') ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-900/50 border border-green-500/50">
                                  <span className="text-green-400 text-xs">✓</span>
                                </span>
                              ) : record.called && (record.call_status === 'in_progress' || record.call_status === 'pending') ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-900/50 border border-yellow-500/50">
                                  <span className="text-yellow-400 text-[10px]">⏳</span>
                                </span>
                              ) : record.called ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-900/50 border border-yellow-500/50">
                                  <span className="text-yellow-400 text-[10px]">⏳</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-800/50 border border-gray-600/50">
                                  <span className="text-gray-500 text-[10px]">-</span>
                                </span>
                              )}
                              </button>
                          </td>
                          
                          {/* FINAL APPROVAL/REJECTION - Compact buttons */}
                          <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-0.5 justify-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openApprovalModal(record.id, 'approve');
                                }}
                                className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                                  record.manual_verification_status === 'verified'
                                    ? 'bg-green-600 text-white shadow-lg ring-1 ring-green-400'
                                    : 'bg-green-900/30 text-green-400 hover:bg-green-900/50 border border-green-500/30'
                                }`}
                                title="Approve Lead (with notes)"
                              >
                                <span className="text-xs">✓</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openApprovalModal(record.id, 'reject');
                                }}
                                className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                                  record.manual_verification_status === 'rejected'
                                    ? 'bg-red-600 text-white shadow-lg ring-1 ring-red-400'
                                    : 'bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-500/30'
                                }`}
                                title="Reject Lead (with notes)"
                              >
                                <span className="text-xs">✗</span>
                              </button>
                            </div>
                          </td>
                          
                          {/* ACTIONS - Compact dropdown/buttons */}
                          <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-0.5 justify-center">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                  handleEditRecord(record);
                                }}
                                className="w-6 h-6 rounded bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 border border-blue-500/30 flex items-center justify-center"
                                title="Edit Record"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                  </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Open modal if has validation data, otherwise expand row
                                        if (record.validation_status || record.validation_results || record.linkedin_profile_data || record.linkedin_company_data) {
                                          setSelectedValidationRecord(record);
                                          setShowValidationDetailModal(true);
                                        } else {
                                  toggleRowExpansion(record.id);
                                        }
                                }}
                                className="w-6 h-6 rounded bg-purple-900/30 text-purple-400 hover:bg-purple-900/50 border border-purple-500/30 flex items-center justify-center"
                                title="View Full Details"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                    </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRecord(record.id);
                                }}
                                className="w-6 h-6 rounded bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-500/30 flex items-center justify-center"
                                title="Delete Record"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                      </tr>
                    {/* Expanded Row - Compact Summary */}
                    {isExpanded && (
                      <tr className="bg-[#0d1117] border-t border-gray-700">
                        <td colSpan="23" className="px-4 py-3">
                          <div className="flex items-center justify-between gap-4">
                            {/* 3-Stage Pipeline - Compact (Validation → Email → Call to match new ordering) */}
                            <div className="flex items-center gap-3">
                              {/* Validation Stage */}
                              <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 cursor-pointer hover:opacity-80 ${
                                (record.validation_stage_status === 'pass' || record.validation_status === 'qualified') ? 'bg-green-500/20 text-green-400' :
                                (record.validation_stage_status === 'reject' || record.validation_status === 'rejected') ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-700/50 text-gray-400'
                              }`}
                              onClick={() => {
                                if (record.validation_status || record.validation_stage_status) {
                                  setSelectedValidationRecord(record);
                                  setShowValidationDetailModal(true);
                                }
                              }}
                              >
                                <span>✅</span>
                                <span className="text-xs font-medium">
                                  {(record.validation_stage_status === 'pass' || record.validation_status === 'qualified') ? 'Qualified' :
                                   (record.validation_stage_status === 'reject' || record.validation_status === 'rejected') ? 'Rejected' :
                                   record.validation_status || 'Not Run'}
                                </span>
                            </div>

                              <span className="text-gray-600">→</span>

                              {/* Email Stage */}
                              <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 cursor-pointer hover:opacity-80 ${
                                record.email_stage_status === 'pass' ? 'bg-green-500/20 text-green-400' :
                                record.email_stage_status === 'reject' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-700/50 text-gray-400'
                              }`}
                              onClick={() => {
                                if (record.emailed || record.email_response || record.email_stage_status) {
                                  setSelectedEmailRecord(record);
                                  setShowEmailResponseModal(true);
                                }
                              }}
                              >
                                <span>📧</span>
                                <span className="text-xs font-medium">
                                  {record.email_stage_status === 'pass' ? 'Pass' :
                                   record.email_stage_status === 'reject' ? 'Reject' :
                                   record.emailed ? 'Waiting' : 'Not Sent'}
                                                  </span>
                                                </div>

                              <span className="text-gray-600">→</span>

                              {/* Call Stage - keep last; status simplified in main row */}
                              <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 cursor-pointer hover:opacity-80 ${
                                record.called ? 'bg-green-500/20 text-green-400' :
                                'bg-gray-700/50 text-gray-400'
                              }`}
                              onClick={() => {
                                if (record.called) {
                                  setSelectedCallRecord(record);
                                }
                              }}
                              >
                                <span>📞</span>
                                <span className="text-xs font-medium">
                                  {record.called ? 'Done' : 'Not Called'}
                                  </span>
                                </div>
                                  </div>

                            {/* View Full Details Button */}
                            <button
                              onClick={() => {
                                setSelectedValidationRecord(record);
                                setShowValidationDetailModal(true);
                              }}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                              View Full Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 flex items-center justify-between border-t-2 border-gray-300">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border-2 border-gray-300 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border-2 border-gray-300 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    Next →
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 font-medium">
                      Showing <span className="font-bold text-gray-900 bg-blue-50 px-2 py-1 rounded">{(currentPage - 1) * recordsPerPage + 1}</span> to{' '}
                      <span className="font-bold text-gray-900 bg-blue-50 px-2 py-1 rounded">{Math.min(currentPage * recordsPerPage, totalFilteredRecords)}</span> of{' '}
                      <span className="font-bold text-gray-900">{totalFilteredRecords}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-lg shadow-md -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-3 py-2 rounded-l-lg border-2 border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border-2 text-sm font-semibold transition-all ${
                              currentPage === pageNum
                                ? 'z-10 bg-gradient-to-r from-blue-600 to-blue-700 border-blue-600 text-white shadow-lg'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-3 py-2 rounded-r-lg border-2 border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Campaign Selection Modal for Email */}
        {showCampaignModal && selectedRecordForEmail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl relative z-[10000]">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">📧 Send Email</h2>
              <div className="mb-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-green-600 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-2">✓ Email address automatically selected from record:</p>
                    <p className="text-lg text-green-800 font-bold">{selectedRecordForEmail.email}</p>
                    {selectedRecordForEmail.full_name && (
                      <p className="text-sm text-green-700 mt-2">👤 {selectedRecordForEmail.full_name}</p>
                    )}
                    {selectedRecordForEmail.company_name && (
                      <p className="text-sm text-green-700">🏢 {selectedRecordForEmail.company_name}</p>
                    )}
                    <p className="text-xs text-green-600 mt-3 italic">No need to manually enter email - it's already taken from this record!</p>
                  </div>
                </div>
              </div>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-1">📋 How it works:</p>
                <ul className="text-xs text-blue-700 list-disc list-inside space-y-1">
                  <li>Select a campaign template below (subject + content)</li>
                  <li>The email will be sent to <strong>{selectedRecordForEmail?.email}</strong> automatically</li>
                  <li><strong>No need to enter recipient emails</strong> - it's already taken from this record!</li>
                </ul>
              </div>
              <p className="text-sm text-gray-700 mb-4 font-medium">
                Select a campaign template below:
              </p>

              {loadingCampaigns ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading campaigns...</p>
                </div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📭</div>
                  <p className="text-gray-600 mb-2 font-semibold">No campaigns found</p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-left">
                    <p className="text-sm text-yellow-800 font-medium mb-2">ℹ️ Important:</p>
                    <p className="text-sm text-yellow-700">
                      When creating a campaign, you'll see a "Recipient Emails" field, but <strong>you don't need to enter emails there</strong> when sending from a task record.
                    </p>
                    <p className="text-sm text-yellow-700 mt-2">
                      The email will be sent to: <strong className="text-yellow-900">{selectedRecordForEmail?.email}</strong> (automatically from this record).
                    </p>
                    <p className="text-xs text-yellow-600 mt-2 italic">
                      Campaigns are templates - you only need to set the subject and content. The recipient email comes from the record automatically.
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">Create a campaign template (subject + content) to send emails</p>
                  <button
                    onClick={() => {
                      setShowCampaignModal(false);
                      window.open('/campaigns/create?fromTask=true', '_blank');
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700"
                  >
                    Create Campaign Template
                  </button>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-2">
                    {campaigns.map((campaign) => (
                      <button
                        key={campaign.id}
                        onClick={() => handleCampaignSelect(campaign)}
                        className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 text-lg">{campaign.campaign_name}</h3>
                            <p className="text-sm text-gray-700 mt-1 font-medium">Subject: {campaign.subject_line}</p>
                            <div className="flex items-center space-x-4 mt-3 text-xs">
                              <span className="px-2 py-1 bg-gray-100 rounded">Total: {campaign.total_recipients || 0}</span>
                              <span className="px-2 py-1 bg-gray-100 rounded">Sent: {campaign.sent_count || 0}</span>
                              <span className={`px-2 py-1 rounded font-medium ${
                                campaign.status === 'completed' ? 'bg-green-100 text-green-800' :
                                campaign.status === 'sending' ? 'bg-yellow-100 text-yellow-800' :
                                campaign.status === 'failed' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {campaign.status || 'draft'}
                              </span>
                            </div>
                            <p className="text-xs text-blue-600 mt-2 font-medium">Click to send email using this campaign →</p>
                          </div>
                          <div className="ml-4 flex items-center">
                            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowCampaignModal(false);
                    setSelectedRecordForEmail(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Upload Records</h2>
              <p className="text-sm text-gray-600 mb-4">
                Upload a CSV file or paste data in JSON/CSV format.
              </p>
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-semibold text-yellow-800 mb-1">Required Fields (records missing either will be skipped):</p>
                <ul className="text-xs text-yellow-700 list-disc list-inside space-y-1">
                  <li><strong>LinkedIn URL Contact</strong> (Required)</li>
                  <li><strong>LinkedIn URL Company</strong> (Required)</li>
                  <li>Full Name, Email, Company Name, State, Phone Number, Company Website (Optional)</li>
                </ul>
              </div>

              {/* Upload Method Selection */}
              <div className="mb-4 flex space-x-4 border-b border-gray-200">
                <button
                  onClick={() => {
                    setUploadMethod('file');
                    setUploadData('');
                    setSelectedFile(null);
                    setError(null);
                  }}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    uploadMethod === 'file'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📁 Upload CSV File
                </button>
                <button
                  onClick={() => {
                    setUploadMethod('paste');
                    setSelectedFile(null);
                    setError(null);
                  }}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    uploadMethod === 'paste'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📋 Paste Data
                </button>
              </div>

              {/* File Upload Section */}
              {uploadMethod === 'file' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="csv-file-input"
                    />
                    <label
                      htmlFor="csv-file-input"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm text-gray-600 mb-1">
                        {selectedFile ? selectedFile.name : 'Click to upload CSV file'}
                      </span>
                      <span className="text-xs text-gray-500">
                        CSV files only
                      </span>
                    </label>
                  </div>
                  {selectedFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                      ✓ File selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </div>
                  )}
                  {uploadData && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preview ({(() => {
                          try {
                            const parsed = JSON.parse(uploadData);
                            return Array.isArray(parsed) ? `${parsed.length} records` : '1 record';
                          } catch {
                            return 'Data';
                          }
                        })()}):
                      </label>
                      <div className="border border-gray-300 rounded-lg bg-gray-50 p-4 max-h-64 overflow-y-auto">
                        {(() => {
                          try {
                            const parsed = JSON.parse(uploadData);
                            const records = Array.isArray(parsed) ? parsed : [parsed];
                            if (records.length === 0) return <p className="text-gray-500 text-sm">No records found</p>;
                            
                            // Get all unique keys from all records
                            const allKeys = [...new Set(records.flatMap(r => Object.keys(r)))];
                            
                            return (
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-xs">
                                  <thead>
                                    <tr className="bg-gray-200">
                                      <th className="px-2 py-1 text-left font-semibold text-gray-700 border-b">#</th>
                                      {allKeys.slice(0, 8).map(key => (
                                        <th key={key} className="px-2 py-1 text-left font-semibold text-gray-700 border-b">
                                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </th>
                                      ))}
                                      {allKeys.length > 8 && (
                                        <th className="px-2 py-1 text-left font-semibold text-gray-700 border-b">
                                          +{allKeys.length - 8} more
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {records.slice(0, 10).map((record, idx) => (
                                      <tr key={idx} className="border-b hover:bg-gray-100">
                                        <td className="px-2 py-1 text-gray-600 font-medium">{idx + 1}</td>
                                        {allKeys.slice(0, 8).map(key => (
                                          <td key={key} className="px-2 py-1 text-gray-700">
                                            {record[key] ? String(record[key]).substring(0, 30) : '-'}
                                          </td>
                                        ))}
                                        {allKeys.length > 8 && (
                                          <td className="px-2 py-1 text-gray-500 text-xs">
                                            ...
                                          </td>
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {records.length > 10 && (
                                  <p className="text-xs text-gray-500 mt-2 text-center">
                                    Showing first 10 of {records.length} records
                                  </p>
                                )}
                              </div>
                            );
                          } catch (e) {
                            return (
                      <textarea
                        value={uploadData}
                        readOnly
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white font-mono text-xs text-gray-900"
                        rows="8"
                      />
                            );
                          }
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Paste Data Section */}
              {uploadMethod === 'paste' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data (JSON array or CSV)
                  </label>
                  <textarea
                    value={uploadData}
                    onChange={(e) => setUploadData(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    rows="15"
                    placeholder='JSON format: [{"linkedin_url_contact":"https://linkedin.com/in/johndoe","linkedin_url_company":"https://linkedin.com/company/acme","full_name":"John Doe","email":"john@example.com","company_name":"Acme Inc"}]

CSV format (LinkedIn URL Contact AND LinkedIn URL Company are REQUIRED):
LinkedIn URL Contact,LinkedIn URL Company,Full Name,Email,Company Name
https://linkedin.com/in/johndoe,https://linkedin.com/company/acme,John Doe,john@example.com,Acme Inc'
                  />
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading || (uploadMethod === 'file' && !selectedFile && !uploadData.trim()) || (uploadMethod === 'paste' && !uploadData.trim())}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload Records'}
                </button>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadData('');
                    setSelectedFile(null);
                    setUploadMethod('paste');
                    setError(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ EDIT RECORD MODAL ============ */}
        {showEditModal && editingRecord && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/70 backdrop-blur-sm" 
              style={{ zIndex: 9999998 }}
              onClick={handleCancelEdit}
            />
            
            {/* Modal */}
            <div 
              className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none overflow-y-auto"
              style={{ zIndex: 9999999 }}
            >
              <div 
                className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border-2 border-blue-500/50 w-full max-w-4xl pointer-events-auto my-8"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="p-6 border-b border-slate-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      Edit Record
                    </h2>
                    <button 
                      onClick={handleCancelEdit}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  {error && (
                    <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg">
                      <p className="text-red-300 text-sm">{error}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* LinkedIn URLs - Full Width */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        LinkedIn URL For Contact <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={editFormData.linkedin_url_contact || editFormData.linkedin_url || ''}
                        onChange={(e) => setEditFormData({...editFormData, linkedin_url_contact: e.target.value, linkedin_url: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="https://www.linkedin.com/in/..."
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        LinkedIn URL For Company
                      </label>
                      <input
                        type="text"
                        value={editFormData.linkedin_url_company || ''}
                        onChange={(e) => setEditFormData({...editFormData, linkedin_url_company: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="https://www.linkedin.com/company/..."
                      />
                    </div>

                    {/* Name Fields */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">First Name</label>
                      <input
                        type="text"
                        value={editFormData.first_name || ''}
                        onChange={(e) => setEditFormData({...editFormData, first_name: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="First Name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Last Name</label>
                      <input
                        type="text"
                        value={editFormData.last_name || ''}
                        onChange={(e) => setEditFormData({...editFormData, last_name: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="Last Name"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name (Optional)</label>
                      <input
                        type="text"
                        value={editFormData.full_name || ''}
                        onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="Full Name"
                      />
                    </div>

                    {/* Email Fields */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Email</label>
                      <input
                        type="email"
                        value={editFormData.email || ''}
                        onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Business Email</label>
                      <input
                        type="email"
                        value={editFormData.business_email || ''}
                        onChange={(e) => setEditFormData({...editFormData, business_email: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="business@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Domain</label>
                      <input
                        type="text"
                        value={editFormData.domain || ''}
                        onChange={(e) => setEditFormData({...editFormData, domain: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="example.com"
                      />
                    </div>

                    {/* Company Fields */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Company Name</label>
                      <input
                        type="text"
                        value={editFormData.company_name || ''}
                        onChange={(e) => setEditFormData({...editFormData, company_name: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="Company Name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Company Website</label>
                      <input
                        type="text"
                        value={editFormData.company_website || ''}
                        onChange={(e) => setEditFormData({...editFormData, company_website: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="https://example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Job Title</label>
                      <input
                        type="text"
                        value={editFormData.job_title || ''}
                        onChange={(e) => setEditFormData({...editFormData, job_title: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="Job Title"
                      />
                    </div>

                    {/* Industry & Size */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Industry Type</label>
                      <input
                        type="text"
                        value={editFormData.industry_type || ''}
                        onChange={(e) => setEditFormData({...editFormData, industry_type: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="Technology"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Revenue Size</label>
                      <input
                        type="text"
                        value={editFormData.revenue_size || ''}
                        onChange={(e) => setEditFormData({...editFormData, revenue_size: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="$1M-$10M"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Employee Size</label>
                      <input
                        type="text"
                        value={editFormData.employee_size || ''}
                        onChange={(e) => setEditFormData({...editFormData, employee_size: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="100-500"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                      <input
                        type="text"
                        value={editFormData.phone_number || ''}
                        onChange={(e) => setEditFormData({...editFormData, phone_number: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="+1234567890"
                      />
                    </div>

                    {/* Address Fields */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Street Address</label>
                      <input
                        type="text"
                        value={editFormData.address_street || ''}
                        onChange={(e) => setEditFormData({...editFormData, address_street: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="123 Main Street"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">City</label>
                      <input
                        type="text"
                        value={editFormData.city || ''}
                        onChange={(e) => setEditFormData({...editFormData, city: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="City"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">State</label>
                      <input
                        type="text"
                        value={editFormData.state || ''}
                        onChange={(e) => setEditFormData({...editFormData, state: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="State"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Zip Code</label>
                      <input
                        type="text"
                        value={editFormData.zip_code || ''}
                        onChange={(e) => setEditFormData({...editFormData, zip_code: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="12345"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Country</label>
                      <input
                        type="text"
                        value={editFormData.country || ''}
                        onChange={(e) => setEditFormData({...editFormData, country: e.target.value})}
                        className="w-full px-4 py-2.5 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
                  <button
                    onClick={handleCancelEdit}
                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white rounded-xl font-semibold transition-all flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ EMAIL SETTINGS MODAL - REDESIGNED ============ */}
        {showEmailSettingsModal && (
          <>
            {/* Backdrop with highest z-index */}
            <div 
              className="fixed top-0 left-0 right-0 bottom-0 bg-black/70" 
              style={{ 
                zIndex: 999999,
                position: 'fixed',
                width: '100vw',
                height: '100vh'
              }}
              onClick={() => setShowEmailSettingsModal(false)}
            />
            
            {/* Modal Container - Even higher z-index */}
            <div 
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              style={{ 
                zIndex: 9999999,
                position: 'fixed'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-3xl shadow-2xl border-2 border-blue-500/50 p-8 m-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-blue-500/30">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/50">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">Bulk Email Campaign</h2>
                      <p className="text-blue-300 text-sm mt-1">Configure and send emails to selected records</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowEmailSettingsModal(false)} 
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 hover:text-red-200 transition-all border border-red-500/30"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Campaign Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-blue-200 mb-3 uppercase tracking-wide">📋 Select Campaign Template</label>
                  <div className="relative">
                    <select
                      value={bulkEmailCampaignId || ''}
                      onChange={(e) => {
                        const campaignId = e.target.value;
                        setBulkEmailCampaignId(campaignId);
                        const campaign = campaigns.find(c => c.id === parseInt(campaignId));
                        if (campaign) {
                          setBulkEmailSubjectLine(campaign.subject_line || '');
                        }
                      }}
                      className="w-full px-5 py-4 bg-slate-800/80 border-2 border-blue-500/40 rounded-2xl focus:ring-4 focus:ring-blue-500/50 focus:border-blue-400 transition-all text-white text-lg font-medium appearance-none cursor-pointer hover:border-blue-400/60"
                      style={{ position: 'relative', zIndex: 10 }}
                    >
                      <option value="" className="bg-slate-800 text-gray-300">-- Select a Campaign --</option>
                      {campaigns.map(campaign => (
                        <option key={campaign.id} value={campaign.id} className="bg-slate-800 text-white py-2">{campaign.campaign_name}</option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Custom Subject Line */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-blue-200 mb-3 uppercase tracking-wide">✏️ Custom Subject Line</label>
                  <input
                    type="text"
                    value={bulkEmailSubjectLine}
                    onChange={(e) => setBulkEmailSubjectLine(e.target.value)}
                    placeholder="Enter subject line with dynamic variables..."
                    className="w-full px-5 py-4 bg-slate-800/80 border-2 border-blue-500/40 rounded-2xl focus:ring-4 focus:ring-blue-500/50 focus:border-blue-400 transition-all text-white text-lg placeholder-gray-400"
                  />
                  <p className="text-xs text-blue-300 mt-2 ml-1">
                    💡 Use variables like {'{first_name}'}, {'{last_name}'}, {'{company_name}'} for personalization
                  </p>
                </div>

                {/* Dynamic Variables */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-blue-200 mb-3 uppercase tracking-wide">🔤 Insert Dynamic Variables</label>
                  <div className="flex flex-wrap gap-2">
                    {['{first_name}', '{last_name}', '{full_name}', '{company_name}', '{job_title}', '{city}', '{state}', '{country}'].map(variable => (
                      <button
                        key={variable}
                        onClick={() => insertVariable(variable)}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-semibold border border-blue-400/50 hover:from-blue-500 hover:to-blue-600 transition-all shadow-lg hover:shadow-blue-500/50 hover:scale-105"
                      >
                        {variable}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                {bulkEmailSubjectLine && (
                  <div className="mb-6 p-5 bg-gradient-to-r from-emerald-900/40 to-blue-900/40 rounded-2xl border-2 border-emerald-500/30">
                    <div className="text-xs font-bold text-emerald-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Preview (with sample data)
                    </div>
                    <p className="text-white font-semibold text-lg">
                      {bulkEmailSubjectLine
                        .replace('{first_name}', 'John')
                        .replace('{last_name}', 'Doe')
                        .replace('{full_name}', 'John Doe')
                        .replace('{company_name}', 'Acme Corp')
                        .replace('{job_title}', 'CEO')
                        .replace('{city}', 'San Francisco')
                        .replace('{state}', 'California')
                        .replace('{country}', 'USA')}
                    </p>
                  </div>
                )}

                {/* Email Filter Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-blue-200 mb-3 uppercase tracking-wide">🎯 Send To</label>
                  <div className="relative">
                    <select
                      value={emailFilter}
                      onChange={(e) => {
                        // Don't allow selecting "selected" if no records are selected
                        if (e.target.value === 'selected' && selectedRecords.length === 0) {
                          alert('Please select records from the table first');
                          return;
                        }
                        setEmailFilter(e.target.value);
                      }}
                      className="w-full px-5 py-4 bg-slate-800/80 border-2 border-blue-500/40 rounded-2xl focus:ring-4 focus:ring-blue-500/50 focus:border-blue-400 transition-all text-white text-lg font-medium appearance-none cursor-pointer hover:border-blue-400/60"
                      style={{ position: 'relative', zIndex: 10 }}
                    >
                      <option value="selected" className="bg-slate-800 text-white">
                        ✔️ Only SELECTED records from dashboard {selectedRecords.length > 0 ? `(${selectedRecords.length} selected)` : '(0 selected - select from table)'}
                      </option>
                      <option value="not-emailed" className="bg-slate-800 text-white">📧 Only records NOT yet emailed</option>
                      <option value="all" className="bg-slate-800 text-white">🔄 ALL records with email (including already emailed)</option>
                      <option value="qualified" className="bg-slate-800 text-white">✅ Only QUALIFIED records (from validation)</option>
                    </select>
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-blue-300 mt-2 ml-1">
                    {emailFilter === 'selected'
                      ? `✔️ Only the ${selectedRecords.length} selected records will receive emails`
                      : emailFilter === 'all' 
                      ? '⚠️ Warning: This will resend emails to records that were already emailed'
                      : emailFilter === 'qualified'
                      ? '✅ Only records marked as "qualified" from validation will receive emails'
                      : '📧 Only records that have not been emailed yet will receive emails'}
                  </p>
                </div>

                {/* Record Count Info */}
                <div className="mb-8 p-5 bg-gradient-to-r from-blue-900/60 to-indigo-900/60 rounded-2xl border-2 border-blue-400/50">
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-white">
                      <span className="text-2xl font-bold text-blue-300">{getEmailableRecordsCount()}</span>
                      <span className="text-lg ml-2">records will receive emails</span>
                      <div className="text-sm text-blue-200 mt-1">
                        ({emailFilter === 'selected'
                          ? `${selectedRecords.length} selected records`
                          : emailFilter === 'all' 
                          ? 'all with email address' 
                          : emailFilter === 'qualified' 
                          ? 'qualified leads only' 
                          : 'not yet emailed'})
                      </div>
                    </div>
                  </div>
                  {emailFilter === 'all' && task?.records?.filter(r => r.emailed).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-500/30 text-amber-300 text-sm flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span>
                        <strong>{task.records.filter(r => r.emailed).length}</strong> records were already emailed and will receive duplicate emails
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  <button
                    onClick={handleBulkEmailSend}
                    disabled={!bulkEmailCampaignId || isBulkProcessing}
                    className="flex-1 px-8 py-5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-2xl text-xl font-bold hover:from-emerald-500 hover:to-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-2xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-105 disabled:hover:scale-100"
                  >
                    {isBulkProcessing ? (
                      <span className="flex items-center justify-center gap-3">
                        <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending Emails...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Start Sending Emails
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setShowEmailSettingsModal(false)}
                    className="px-8 py-5 border-2 border-red-500/50 bg-red-900/30 rounded-2xl text-red-200 text-xl font-bold hover:bg-red-900/50 hover:border-red-400 transition-all shadow-lg hover:scale-105"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ VALIDATION MODAL ============ */}
        {showValidationModal && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed top-0 left-0 right-0 bottom-0 bg-black/70" 
              style={{ zIndex: 999999 }}
              onClick={() => !isValidating && setShowValidationModal(false)}
            />
            
            {/* Modal */}
            <div 
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              style={{ zIndex: 9999999 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 rounded-3xl shadow-2xl border-2 border-emerald-500/50 p-8 m-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-emerald-500/30">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/50">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">Lead Validation</h2>
                      <p className="text-emerald-300 text-sm mt-1">Validate leads using AI and data sources</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => !isValidating && setShowValidationModal(false)} 
                    disabled={isValidating}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 hover:text-red-200 transition-all border border-red-500/30 disabled:opacity-50"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Info Panel */}
                <div className="mb-6 p-5 bg-gradient-to-r from-emerald-900/40 to-blue-900/40 rounded-2xl border border-emerald-500/30">
                  <h3 className="text-lg font-semibold text-emerald-200 mb-3">🔍 Validation Process:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-300">1.</span>
                      <div className="text-gray-300">Scrape LinkedIn Profile (BrightData)</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-300">2.</span>
                      <div className="text-gray-300">Scrape LinkedIn Company Data</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-300">3.</span>
                      <div className="text-gray-300">Verify Person Works at Company</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-300">4.</span>
                      <div className="text-gray-300">Check Location Match</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-300">5.</span>
                      <div className="text-gray-300">Deep Research (Perplexity AI) with Rules</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-300">6.</span>
                      <div className="text-gray-300">Scrape Company Website</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-300">7.</span>
                      <div className="text-gray-300">Validate Toll-Free Numbers</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-300">8.</span>
                      <div className="text-gray-300">Apply Validation Rules</div>
                    </div>
                  </div>
                </div>

                {/* Validation Rules Upload Section */}
                <div className="mb-6 p-5 bg-gradient-to-r from-amber-900/40 to-orange-900/40 rounded-2xl border-2 border-amber-500/50">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-amber-200 mb-1">📜 Validation Rules (rule.txt)</h3>
                      <p className="text-xs text-amber-300">Upload validation rules file - Rules will be used in Perplexity AI deep search</p>
                    </div>
                    {validationFileName && (
                      <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-lg text-xs font-semibold">
                        ✓ {validationFileName}
                      </span>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-amber-200 mb-2">
                      Upload rule.txt file
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".txt"
                        onChange={handleValidationFileUpload}
                        className="hidden"
                        id="validation-rules-upload"
                      />
                      <label
                        htmlFor="validation-rules-upload"
                        className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-slate-800/80 border-2 border-amber-500/40 rounded-xl cursor-pointer hover:border-amber-400 transition-all text-white font-medium"
                      >
                        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span>{validationFileName ? 'Change Rules File' : 'Choose rule.txt file'}</span>
                      </label>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-amber-300/90">Download sample rule .txt:</span>
                      <button
                        type="button"
                        onClick={handleDownloadSampleRules}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-slate-700/80 text-amber-200 border border-amber-500/40 hover:bg-slate-600/80 hover:border-amber-400/60 transition-colors"
                      >
                        <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download sample rule .txt
                      </button>
                    </div>
                  </div>

                  {validationRules && (
                    <div className="mt-4 p-4 bg-slate-900/60 rounded-xl border border-amber-500/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-amber-300 uppercase">CURRENT RULES PREVIEW</span>
                        <span className="text-xs text-amber-400">{validationRules.split('\n').length} lines</span>
                      </div>
                      <div className="max-h-32 overflow-y-auto text-xs text-gray-300 font-mono bg-slate-950/50 p-3 rounded border border-slate-700">
                        {validationRules.split('\n').slice(0, 10).map((line, idx) => (
                          <div key={idx} className="mb-1">{line || ' '}</div>
                        ))}
                        {validationRules.split('\n').length > 10 && (
                          <div className="text-amber-400 italic">... and {validationRules.split('\n').length - 10} more lines</div>
                        )}
                      </div>
                      <p className="text-xs text-amber-300 mt-2 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        These rules will be used in Perplexity AI deep search to filter and qualify leads
                      </p>
                    </div>
                  )}

                  {!validationRules && (
                    <div className="mt-4 p-4 bg-yellow-900/30 rounded-xl border border-yellow-500/30">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="text-sm text-yellow-300">
                          <p className="font-semibold mb-1">No validation rules uploaded</p>
                          <p className="text-xs">Upload a rule.txt file to enable rule-based filtering. Rules will be used in Perplexity AI deep search to evaluate each lead.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Validation Filter Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-emerald-200 mb-3 uppercase tracking-wide">🎯 Validate</label>
                  <div className="relative">
                    <select
                      value={validationFilter}
                      onChange={(e) => {
                        const newFilter = e.target.value;
                        
                        // CRITICAL: Validate selection before allowing change to 'selected'
                        if (newFilter === 'selected') {
                          if (!selectedRecords || selectedRecords.length === 0) {
                            alert('⚠️ Please select at least one record from the table first before choosing "Only SELECTED records"');
                            // Force dropdown back to previous value
                            setTimeout(() => {
                              e.target.value = validationFilter;
                            }, 0);
                            return; // Don't change the filter
                          }
                        }
                        
                        // Update filter
                        const oldFilter = validationFilter;
                        setValidationFilter(newFilter);
                        console.log(`📋 Validation filter changed: ${oldFilter} → ${newFilter}`);
                        if (newFilter === 'selected') {
                          console.log(`   ✅ Selected records count: ${selectedRecords.length}`);
                          console.log(`   ✅ Selected record IDs:`, selectedRecords);
                        } else {
                          console.log(`   ✅ Filter set to: ${newFilter}`);
                        }
                      }}
                      className="w-full px-5 py-4 bg-slate-800/80 border-2 border-emerald-500/40 rounded-2xl focus:ring-4 focus:ring-emerald-500/50 focus:border-emerald-400 transition-all text-white text-lg font-medium appearance-none cursor-pointer hover:border-emerald-400/60"
                      style={{ position: 'relative', zIndex: 10 }}
                    >
                      <option 
                        value="selected" 
                        className="bg-slate-800 text-white"
                        disabled={!selectedRecords || selectedRecords.length === 0}
                        style={{ 
                          opacity: (!selectedRecords || selectedRecords.length === 0) ? 0.5 : 1,
                          cursor: (!selectedRecords || selectedRecords.length === 0) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        ✔️ Only SELECTED records from dashboard {selectedRecords && selectedRecords.length > 0 ? `(${selectedRecords.length} selected)` : '(0 selected - select from table)'}
                      </option>
                      <option value="all" className="bg-slate-800 text-white">🔄 ALL records</option>
                      <option value="verified" className="bg-slate-800 text-white">✅ Only VERIFIED records (manual verification)</option>
                      <option value="unverified" className="bg-slate-800 text-white">⏳ Only UNVERIFIED records (not verified/rejected)</option>
                    </select>
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-emerald-300 mt-2 ml-1">
                    {validationFilter === 'selected'
                      ? `✔️ Only the ${selectedRecords.length} selected records will be validated`
                      : validationFilter === 'all' 
                      ? '🔄 All records in the task will be validated'
                      : validationFilter === 'verified'
                      ? '✅ Only records marked as "verified" (manual verification) will be validated'
                      : '⏳ Only records that are not verified or rejected will be validated'}
                  </p>
                </div>

                {/* Record Count Info */}
                <div className="mb-8 p-5 bg-gradient-to-r from-emerald-900/60 to-blue-900/60 rounded-2xl border-2 border-emerald-400/50">
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-white">
                      <span className="text-2xl font-bold text-emerald-300">{getValidatableRecordsCount()}</span>
                      <span className="text-lg ml-2">records will be validated</span>
                      <div className="text-sm text-emerald-200 mt-1">
                        ({validationFilter === 'selected'
                          ? `${selectedRecords.length} selected records`
                          : validationFilter === 'all' 
                          ? 'all records' 
                          : validationFilter === 'verified' 
                          ? 'verified leads only' 
                          : 'unverified leads only'})
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  {!isValidating ? (
                    <>
                      <button
                        onClick={handleStartValidation}
                        disabled={getValidatableRecordsCount() === 0 || (validationCredits !== null && validationCredits <= 0)}
                        className="flex-1 px-8 py-5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-2xl text-xl font-bold hover:from-emerald-500 hover:to-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-2xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-105 disabled:hover:scale-100 flex items-center justify-center gap-3"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Start Validation
                      </button>
                      <button
                        onClick={() => setShowValidationModal(false)}
                        className="px-8 py-5 border-2 border-red-500/50 bg-red-900/30 rounded-2xl text-red-200 text-xl font-bold hover:bg-red-900/50 hover:border-red-400 transition-all shadow-lg hover:scale-105"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <div className="flex-1 px-8 py-5 bg-emerald-900/50 rounded-2xl text-center text-emerald-200">
                      <div className="flex items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-400"></div>
                        <span className="text-xl font-semibold">Validation in progress... Please wait</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ CALLING AGENT MODAL ============ */}
        {showCallingModal && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed top-0 left-0 right-0 bottom-0 bg-black/70" 
              style={{ zIndex: 999999 }}
              onClick={() => !isBulkProcessing && setShowCallingModal(false)}
            />
            
            {/* Modal */}
            <div 
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              style={{ zIndex: 9999999 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-3xl shadow-2xl border-2 border-purple-500/50 p-8 m-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-purple-500/30">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/50">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">AI Calling Agent</h2>
                      <p className="text-purple-300 text-sm mt-1">Powered by RetellAI • Sequential calls with AI verification</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => !isBulkProcessing && setShowCallingModal(false)} 
                    disabled={isBulkProcessing}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 hover:text-red-200 transition-all border border-red-500/30 disabled:opacity-50"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Info Panel */}
                <div className="mb-6 p-5 bg-gradient-to-r from-purple-900/40 to-indigo-900/40 rounded-2xl border border-purple-500/30">
                  <h3 className="text-lg font-semibold text-purple-200 mb-3">📞 Call Verification Process:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">1️⃣</span>
                      <div>
                        <div className="text-white font-medium">Identity Verification</div>
                        <div className="text-purple-300 text-xs">AI asks: "Am I speaking with [Name]?"</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">2️⃣</span>
                      <div>
                        <div className="text-white font-medium">Interest Check</div>
                        <div className="text-purple-300 text-xs">AI asks about interest in our service</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-purple-500/30">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-400">✅ Both Yes</span>
                      <span className="text-purple-300">=</span>
                      <span className="text-green-400 font-bold">QUALIFIED LEAD</span>
                      <span className="mx-3 text-purple-500">|</span>
                      <span className="text-red-400">❌ Any No</span>
                      <span className="text-purple-300">=</span>
                      <span className="text-red-400 font-bold">REJECTED</span>
                    </div>
                  </div>
                </div>

                {/* Records to Call */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-purple-200">
                      Records to Call: {(() => {
                        if (selectedRecords.length > 0) {
                          const selectedWithPhone = task?.records?.filter(r => 
                            selectedRecords.includes(r.id) && 
                            r.phone_number && 
                            r.phone_number.trim() !== ''
                          ).length || 0;
                          return `${selectedWithPhone} selected${selectedWithPhone !== selectedRecords.length ? ` (${selectedRecords.length - selectedWithPhone} without phone)` : ''}`;
                        } else {
                          const available = task?.records?.filter(r => r.phone_number && !r.called && r.phone_number.trim() !== '').length || 0;
                          return `${available} with phone (not called)`;
                        }
                      })()}
                    </h3>
                    <div className="text-sm text-purple-300">
                      Credits: <span className="text-white font-bold">{credits}</span>
                    </div>
                  </div>
                  {selectedRecords.length > 0 && (
                    <p className="text-xs text-purple-300/70 mt-1">
                      💡 Only selected records with valid phone numbers will be called
                    </p>
                  )}
                </div>

                {/* Progress */}
                {isBulkProcessing && (
                  <div className="mb-6 p-5 bg-slate-800/80 rounded-2xl border border-purple-500/30">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-semibold">Progress: {callingProgress.current}/{callingProgress.total}</span>
                      <span className="text-purple-300 text-sm">{callingProgress.status}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-purple-400 h-4 rounded-full transition-all duration-500"
                        style={{ width: `${(callingProgress.current / callingProgress.total) * 100}%` }}
                      />
                    </div>
                    {callingProgress.currentRecord && (
                      <div className="mt-3 text-sm text-purple-200">
                        Currently calling: <span className="text-white font-medium">
                          {callingProgress.currentRecord.first_name} {callingProgress.currentRecord.last_name}
                        </span> - {callingProgress.currentRecord.phone_number}
                      </div>
                    )}
                  </div>
                )}

                {/* Call Results */}
                {callingResults.length > 0 && (
                  <div className="mb-6 max-h-60 overflow-y-auto">
                    <h3 className="text-lg font-semibold text-purple-200 mb-3">Call Results:</h3>
                    <div className="space-y-2">
                      {callingResults.map((result, index) => (
                        <div 
                          key={index} 
                          className={`p-3 rounded-xl flex items-center justify-between ${
                            result.success 
                              ? 'bg-green-900/30 border border-green-500/30' 
                              : 'bg-red-900/30 border border-red-500/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`text-xl ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                              {result.success ? '✅' : '❌'}
                            </span>
                            <div>
                              <div className="text-white font-medium">{result.name}</div>
                              <div className="text-gray-400 text-xs">{result.phone}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-sm ${result.success ? 'text-green-300' : 'text-red-300'}`}>
                              {result.message}
                            </div>
                            {result.callId && (
                              <div className="text-xs text-purple-300">ID: {result.callId}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-4">
                  {!isBulkProcessing ? (
                    <>
                      <button
                        onClick={handleStartCalling}
                        disabled={credits !== null && credits <= 0}
                        className="flex-1 px-8 py-5 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-2xl text-xl font-bold hover:from-purple-500 hover:to-purple-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-2xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 disabled:hover:scale-100 flex items-center justify-center gap-3"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Start Calling (Sequential)
                      </button>
                      <button
                        onClick={() => setShowCallingModal(false)}
                        className="px-8 py-5 border-2 border-gray-500/50 bg-gray-900/30 rounded-2xl text-gray-200 text-xl font-bold hover:bg-gray-900/50 hover:border-gray-400 transition-all shadow-lg hover:scale-105"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <div className="flex-1 px-8 py-5 bg-purple-900/50 rounded-2xl text-center text-purple-200">
                      <div className="flex items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-400"></div>
                        <span className="text-xl font-semibold">Calling in progress... Please wait</span>
                      </div>
                      <p className="text-sm mt-2 text-purple-300">Each call completes before the next one starts</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ CALL DETAILS MODAL ============ */}
        {selectedCallRecord && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed top-0 left-0 right-0 bottom-0 bg-black/70" 
              style={{ zIndex: 9999998 }}
              onClick={() => setSelectedCallRecord(null)}
            />
            
            {/* Modal */}
            <div 
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
              style={{ zIndex: 99999999 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-3xl shadow-2xl border-2 border-indigo-500/50 p-8 m-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-indigo-500/30">
                    <h2 className="text-2xl font-bold text-white">📞 Call Details</h2>
                    <button 
                      onClick={() => setSelectedCallRecord(null)} 
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Contact Info */}
                  <div className="mb-6 p-4 bg-slate-800/50 rounded-xl">
                    <h3 className="text-lg font-semibold text-indigo-200 mb-3">Contact Information</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-400">Name:</span>
                        <span className="text-white ml-2">{selectedCallRecord.first_name} {selectedCallRecord.last_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Phone:</span>
                        <span className="text-white ml-2">{selectedCallRecord.phone_number}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Company:</span>
                        <span className="text-white ml-2">{selectedCallRecord.company_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Call Status:</span>
                        {/*
                          Simplified to two main dashboard states:
                          - DONE  (green) when the call has finished
                          - PENDING (yellow) when the call is still in progress / not finished
                        */}
                        {(() => {
                          const isDone = selectedCallRecord.called && (selectedCallRecord.call_stage_status === 'pass' ||
                            selectedCallRecord.call_stage_status === 'reject' ||
                            selectedCallRecord.call_status === 'completed' ||
                            selectedCallRecord.call_status === 'success');
                          const label = isDone ? 'DONE' : (selectedCallRecord.called ? 'PENDING' : 'NOT CALLED');
                          const color =
                            !selectedCallRecord.called ? 'text-gray-400' :
                            isDone ? 'text-green-400' : 'text-yellow-400';
                          return (
                            <span className={`ml-2 font-semibold ${color}`}>
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    {callDetails && (
                      <div className="mt-4 pt-4 border-t border-indigo-500/30 grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-gray-500">Call ID:</span>
                          <span className="text-gray-300 ml-2 font-mono">{callDetails.call_id || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Duration:</span>
                          <span className="text-gray-300 ml-2">{callDetails.call_duration ? `${callDetails.call_duration}s` : 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Status:</span>
                          <span className="text-gray-300 ml-2">{callDetails.status || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Called:</span>
                          <span className="text-gray-300 ml-2">{callDetails.created_at ? new Date(callDetails.created_at).toLocaleString() : 'N/A'}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Call Recording */}
                  {loadingCallDetails ? (
                    <div className="mb-6 p-4 bg-slate-800/50 rounded-xl text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400 mx-auto"></div>
                      <p className="text-indigo-300 mt-2">Loading call details...</p>
                    </div>
                  ) : callDetails?.recording_url ? (
                    <div className="mb-6 p-4 bg-slate-800/50 rounded-xl">
                      <h3 className="text-lg font-semibold text-indigo-200 mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                        Call Recording
                      </h3>
                      <div className="bg-slate-900/50 rounded-lg p-4">
                        <audio 
                          controls 
                          className="w-full"
                          src={callDetails.recording_url}
                        >
                          Your browser does not support the audio element.
                        </audio>
                        <div className="mt-3 flex items-center justify-between text-sm">
                          <span className="text-gray-400">Duration: {callDetails.call_duration ? `${callDetails.call_duration}s` : 'N/A'}</span>
                          <a 
                            href={callDetails.recording_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download Recording
                          </a>
                        </div>
                      </div>
                    </div>
                  ) : callDetails && !callDetails.recording_url ? (
                    <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-xl">
                      <p className="text-yellow-300 text-sm">⚠️ Recording not available yet. It may still be processing.</p>
                    </div>
                  ) : null}

                {/* Call Summary */}
                {(() => {
                  // Try multiple sources: call_summary (RetellAI), summary from API, call_report, webhook_response
                  const summary = callDetails?.call_summary ||
                                 callDetails?.call_analysis?.call_summary ||
                                 callDetails?.summary ||
                                 callDetails?.call_report?.summary ||
                                 callDetails?.webhook_response?.call?.call_analysis?.call_summary ||
                                 callDetails?.webhook_response?.call_report?.summary ||
                                 callDetails?.webhook_response?.summary ||
                                 null;
                  return summary ? (
                    <div className="mb-6 p-4 bg-slate-800/50 rounded-xl">
                      <h3 className="text-lg font-semibold text-indigo-200 mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Call Summary
                      </h3>
                      <div className="bg-slate-900/50 rounded-lg p-4">
                        <div className="text-gray-200 text-sm leading-relaxed">
                          {typeof summary === 'string' 
                            ? summary 
                            : JSON.stringify(summary, null, 2)}
                        </div>
                      </div>
                    </div>
                  ) : callDetails && !summary ? (
                    <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-xl">
                      <p className="text-yellow-300 text-sm">⚠️ Summary not available yet. It may still be processing.</p>
                    </div>
                  ) : null;
                })()}

                {/* Full Transcription */}
                {(() => {
                  // Try multiple sources: transcript (RetellAI), full_conversation from API, call_report, webhook_response
                  const fullConversation = callDetails?.transcript ||
                                          callDetails?.full_conversation ||
                                          callDetails?.call_report?.full_conversation || 
                                          callDetails?.call_report?.conversation ||
                                          callDetails?.webhook_response?.call?.transcript ||
                                          callDetails?.webhook_response?.call_report?.full_conversation ||
                                          callDetails?.webhook_response?.call_report?.conversation ||
                                          callDetails?.transcription || 
                                          null;
                  
                  let conversationText = fullConversation;
                  
                  // If it's a JSON array string, parse it
                  if (typeof fullConversation === 'string' && fullConversation.trim().startsWith('[')) {
                    try {
                      const parsed = JSON.parse(fullConversation);
                      if (Array.isArray(parsed)) {
                        conversationText = parsed.join('\n\n');
                      }
                    } catch (e) {
                      // Keep as string if parsing fails
                    }
                  }
                  
                  return conversationText ? (
                    <div className="mb-6 p-4 bg-slate-800/50 rounded-xl">
                      <h3 className="text-lg font-semibold text-indigo-200 mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Full Transcription
                      </h3>
                      <div className="bg-slate-900/50 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <div className="text-gray-200 text-sm whitespace-pre-wrap leading-relaxed">
                          {typeof conversationText === 'string' 
                            ? conversationText 
                            : JSON.stringify(conversationText, null, 2)}
                        </div>
                      </div>
                    </div>
                  ) : callDetails && !conversationText ? (
                    <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-xl">
                      <p className="text-yellow-300 text-sm">⚠️ Full transcription not available yet. It may still be processing.</p>
                    </div>
                  ) : null;
                })()}

                {/* AI Verification / Final verdict section removed.
                    Only recording + full transcription are shown for calls. */}

                {/* Close Button */}
                <button
                  onClick={() => setSelectedCallRecord(null)}
                  className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-xl font-semibold hover:from-indigo-500 hover:to-indigo-400 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}

        {/* ============ FINAL APPROVAL/REJECTION MODAL ============ */}
        {showApprovalModal && approvalRecordId && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/70 backdrop-blur-sm" 
              style={{ zIndex: 9999998 }}
              onClick={() => {
                setShowApprovalModal(false);
                setApprovalNotes('');
                setApprovalRecordId(null);
                setApprovalAction(null);
              }}
            />
            
            {/* Modal */}
            <div 
              className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none"
              style={{ zIndex: 9999999 }}
            >
              <div 
                className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border-2 border-blue-500/50 w-full max-w-lg pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="p-6 border-b border-slate-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        approvalAction === 'approve' 
                          ? 'bg-gradient-to-r from-green-500 to-green-600' 
                          : 'bg-gradient-to-r from-red-500 to-red-600'
                      }`}>
                        {approvalAction === 'approve' ? (
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      {approvalAction === 'approve' ? 'Approve Lead' : 'Reject Lead'}
                    </h2>
                    <button 
                      onClick={() => {
                        setShowApprovalModal(false);
                        setApprovalNotes('');
                        setApprovalRecordId(null);
                        setApprovalAction(null);
                      }}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Record Info */}
                  {(() => {
                    const record = task?.records?.find(r => r.id === approvalRecordId);
                    if (!record) return null;
                    return (
                      <div className="mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                            {(record.first_name?.[0] || record.full_name?.[0] || '?').toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-semibold">
                              {record.first_name && record.last_name 
                                ? `${record.first_name} ${record.last_name}`
                                : record.full_name || 'Unknown'}
                            </p>
                            <p className="text-gray-400 text-sm">{record.company_name || 'No company'}</p>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <span className="text-gray-300 ml-2">{record.email || record.business_email || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>
                            <span className="text-gray-300 ml-2">{record.phone_number || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Notes Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Justification Notes {approvalAction === 'approve' ? '(Optional)' : '(Required)'}
                    </label>
                    <textarea
                      value={approvalNotes}
                      onChange={(e) => setApprovalNotes(e.target.value)}
                      placeholder={approvalAction === 'approve' 
                        ? 'Add notes about why this lead is approved (optional)...'
                        : 'Explain why this lead is rejected (required)...'}
                      className="w-full px-4 py-3 bg-slate-800 border-2 border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                      rows={4}
                      required={approvalAction === 'reject'}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {approvalAction === 'reject' 
                        ? 'Please provide a reason for rejection to help improve lead quality.'
                        : 'Notes help track why leads were approved for future reference.'}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowApprovalModal(false);
                        setApprovalNotes('');
                        setApprovalRecordId(null);
                        setApprovalAction(null);
                      }}
                      className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFinalApproval}
                      disabled={approvalAction === 'reject' && !approvalNotes.trim()}
                      className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                        approvalAction === 'approve'
                          ? 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white'
                          : 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      {approvalAction === 'approve' ? (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Approve Lead
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Reject Lead
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ VALIDATION MODAL ============ */}
        {showValidationModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  Lead Validation
                </h2>
                <button onClick={() => setShowValidationModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Validation Steps Info */}
              <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                <h3 className="font-semibold text-emerald-900 mb-3">🔍 8-Step Validation Process:</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-emerald-800">
                  <li>Scrape LinkedIn Profile (BrightData API)</li>
                  <li>Scrape LinkedIn Company Data</li>
                  <li>Verify Person Works at Company</li>
                  <li>Check Location Match</li>
                  <li>Deep Research (Perplexity AI) with Rules</li>
                  <li>Scrape Company Website</li>
                  <li>Validate Toll-Free Numbers</li>
                  <li>Apply Validation Rules</li>
                </ol>
              </div>

              {/* Upload Validation Rules */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Validation Rules File</label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-emerald-400 transition-all text-center">
                      <input
                        type="file"
                        accept=".txt"
                        onChange={handleValidationFileUpload}
                        className="hidden"
                      />
                      <p className="text-gray-600 text-sm">
                        {validationFileName || 'Click to upload validation rules (.txt)'}
                      </p>
                    </div>
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-500">Download sample rule .txt:</span>
                  <button
                    type="button"
                    onClick={handleDownloadSampleRules}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download sample rule .txt
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Format: One rule per line (e.g., "country: India", "industry: Technology")
                </p>
              </div>

              {/* Current Rules Preview */}
              {validationRules && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Current Rules</label>
                  <textarea
                    value={validationRules}
                    onChange={(e) => setValidationRules(e.target.value)}
                    className="w-full h-32 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Enter validation rules here..."
                  />
                </div>
              )}

              {/* Status Info */}
              {validationStatus && (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{validationStatus.total}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-600">{validationStatus.qualified}</div>
                      <div className="text-xs text-emerald-600">Qualified</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">{validationStatus.rejected}</div>
                      <div className="text-xs text-red-600">Rejected</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-400">{validationStatus.pending}</div>
                      <div className="text-xs text-gray-400">Pending</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleStartValidation}
                  disabled={isValidating || (validationCredits !== null && validationCredits <= 0)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl font-semibold hover:from-emerald-500 hover:to-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2"
                >
                  {isValidating ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Validating...
                    </>
                  ) : (
                    'Start Validation'
                  )}
                </button>
                <button
                  onClick={() => setShowValidationModal(false)}
                  className="px-6 py-3 border-2 border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ MASTER QUEUE MODAL ============ */}
        {showMasterQueueModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  Master Queue
                </h2>
                <button onClick={() => setShowMasterQueueModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Info */}
              <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <p className="text-amber-800 text-sm">
                  Master Queue will execute selected operations sequentially. Each operation will complete before the next one starts.
                </p>
              </div>

              {/* Queue Steps */}
              <div className="mb-6 space-y-3">
                {masterQueueSteps.map((step, index) => (
                  <div 
                    key={step.id}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      step.enabled 
                        ? 'bg-white border-gray-200 hover:border-amber-300' 
                        : 'bg-gray-50 border-gray-100 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={step.enabled}
                            onChange={() => setMasterQueueSteps(prev => prev.map(s => 
                              s.id === step.id ? { ...s, enabled: !s.enabled } : s
                            ))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                            {index + 1}
                          </span>
                          <span className="font-semibold text-gray-900">{step.name}</span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        step.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        step.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                        step.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {step.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Email Settings for Master Queue */}
              {masterQueueSteps.find(s => s.id === 'email')?.enabled && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <label className="block text-sm font-semibold text-blue-900 mb-2">Email Campaign</label>
                  <select
                    value={bulkEmailCampaignId || ''}
                    onChange={(e) => setBulkEmailCampaignId(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  >
                    <option value="">-- Select Campaign --</option>
                    {campaigns.map(c => (
                      <option key={c.id} value={c.id}>{c.campaign_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleMasterQueueStart}
                  disabled={isBulkProcessing || !masterQueueSteps.some(s => s.enabled)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white rounded-xl font-bold hover:from-amber-400 hover:via-orange-400 hover:to-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-orange-500/25"
                >
                  {isBulkProcessing ? 'Processing...' : 'Start Master Queue'}
                </button>
                <button
                  onClick={() => setShowMasterQueueModal(false)}
                  className="px-6 py-3 border-2 border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ VALIDATION DETAIL MODAL ============ */}
        <ValidationDetailModal
          isOpen={showValidationDetailModal}
          onClose={() => {
            setShowValidationDetailModal(false);
            setSelectedValidationRecord(null);
          }}
          record={selectedValidationRecord}
          validationResults={selectedValidationRecord?.validation_results}
          taskId={id}
        />

        {/* ============ EMAIL RESPONSE MODAL ============ */}
        <EmailResponseModal
          isOpen={showEmailResponseModal}
          onClose={() => {
            setShowEmailResponseModal(false);
            setSelectedEmailRecord(null);
          }}
          record={selectedEmailRecord}
        />

        {/* ============ EDIT TASK MODAL ============ */}
        {showEditTaskModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1f3a] border border-blue-500/30 rounded-lg p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-white mb-6">Edit Task</h2>
              <form onSubmit={handleUpdateTask}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Task Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={editTaskData.task_name}
                    onChange={(e) => setEditTaskData({ ...editTaskData, task_name: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., Lead Generation Q1 2024"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={editTaskData.description}
                    onChange={(e) => setEditTaskData({ ...editTaskData, description: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
                    rows="3"
                    placeholder="Describe what this task is for..."
                  />
                </div>
                
                {/* Call Configuration Fields */}
                <div className="mb-4 border-t border-gray-700/50 pt-4">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Call Configuration</h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                       Retell AI Agent (Model)
                    </label>
                    <select
                      value={editTaskData.calling_by || ''}
                      onChange={(e) => {
                        setEditTaskData({
                          ...editTaskData,
                          calling_by: e.target.value
                        });
                      }}
                      className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                    >
                      {loadingAgents ? (
                        <option>Loading agents...</option>
                      ) : (
                        <>
                          <option value="">— Select an agent —</option>
                          {retellAgents.length > 0 && (
                            <optgroup label="Retell AI Agents">
                              {retellAgents.map(agent => (
                                <option key={agent.agent_id} value={agent.agent_id}>
                                  {formatRetellAgentLabel(agent)}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {retellAgents.length === 0 && (
                            <option value="" disabled>No agents found from Retell</option>
                          )}
                        </>
                      )}
                    </select>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={refreshRetellData}
                        disabled={loadingAgents || loadingPhoneNumbers}
                        className="px-2 py-1 text-xs font-medium text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded hover:bg-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingAgents || loadingPhoneNumbers ? 'Refreshing...' : 'Refresh Retell Data'}
                      </button>
                      <p className="text-xs text-gray-400">Sync agents and phone numbers from Retell.</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Outbound Phone Number
                    </label>
                    <select
                      value={editTaskData.retell_from_number || ''}
                      onChange={(e) => setEditTaskData({ ...editTaskData, retell_from_number: e.target.value })}
                      className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                    >
                      <option value="">— Default (Global Settings) —</option>
                      {loadingPhoneNumbers ? (
                        <option value="" disabled>Loading phone numbers...</option>
                      ) : (
                        retellPhoneNumbers.map((num) => (
                          <option key={num.phone_number} value={num.phone_number}>
                            {num.nickname ? `${num.nickname} (${num.phone_number})` : num.phone_number}
                          </option>
                        ))
                      )}
                    </select>
                    <p className="mt-1 text-xs text-gray-400">Fetched from Retell when you click Refresh Retell Data.</p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Agent Name <span className="text-gray-500">{"{{agent_name}}"}</span>
                    </label>
                    <input
                      type="text"
                      value={editTaskData.agent_name || ''}
                      onChange={(e) => setEditTaskData({ ...editTaskData, agent_name: e.target.value })}
                      className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      placeholder="Sarah"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      The name the agent will use (e.g., "Sarah").
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Purpose of Call <span className="text-gray-500">{"{{purpose_of_call}}"}</span>
                    </label>
                    <select
                      value={editTaskData.purpose_of_call || 'Discovery Call'}
                      onChange={(e) => setEditTaskData({ ...editTaskData, purpose_of_call: e.target.value })}
                      className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                    >
                      <option value="Discovery Call">Discovery Call</option>
                      <option value="Qualification Call">Qualification Call</option>
                      <option value="Follow-up Call">Follow-up Call</option>
                      <option value="Sales Pitch">Sales Pitch</option>
                      <option value="Appointment Setting">Appointment Setting</option>
                      <option value="Customer Support">Customer Support</option>
                      <option value="Feedback Collection">Feedback Collection</option>
                      <option value="Custom">Custom Purpose...</option>
                    </select>
                    {editTaskData.purpose_of_call === 'Custom' && (
                      <input
                        type="text"
                        value={editTaskData.custom_purpose || ''}
                        onChange={(e) => setEditTaskData({ ...editTaskData, custom_purpose: e.target.value })}
                        className="mt-2 w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="Enter custom purpose..."
                      />
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Caller Company <span className="text-gray-500">([Caller_Company])</span>
                    </label>
                    <input
                      type="text"
                      value={editTaskData.caller_company}
                      onChange={(e) => setEditTaskData({ ...editTaskData, caller_company: e.target.value })}
                      className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      placeholder="The Tech Marketer"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Used in: &quot;Hello — this is [Calling_By] calling from The [Caller_Company].&quot;
                    </p>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Assets <span className="text-gray-500">([assets])</span>
                    </label>
                    <input
                      type="text"
                      value={editTaskData.assets}
                      onChange={(e) => setEditTaskData({ ...editTaskData, assets: e.target.value })}
                      className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      placeholder="Signal-First Demand Generation content from Market Wavegen"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Used in: &quot;Just to confirm — did you interact with the [assets]?&quot;
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Script (Optional)
                    </label>
                    <textarea
                      value={editTaskData.script || ''}
                      onChange={(e) => setEditTaskData({ ...editTaskData, script: e.target.value })}
                      className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-y"
                      rows={8}
                      placeholder={`- Caller company details and background\n- Customer company details and context\n- Purpose of the call and expected outcome\n- Product/service details to mention\n- Asset details to reference in conversation\n- Objection handling notes (optional)`}
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Use this as your call script guidance. Keep it natural and easy for the agent to follow.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditTaskModal(false);
                      setError(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updatingTask}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updatingTask ? 'Updating...' : 'Update Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskDetail;
